import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, MapPin } from "lucide-react";
import {
  formatShortMonthDateRange,
  getLocalEndpointInfo,
  tMinusCount,
  tPlusCount,
} from "../../../utils/dateUtils";

const TaleCard = ({ tale, onClick }) => {
  const navigate = useNavigate();
  const [imageError, setImageError] = useState(false);
  const today = new Date();
  // placeholder SVG for missing images
  const PlaceholderImage = () => (
    <svg
      viewBox="0 0 573.49 477.156"
      enableBackground="new 0 0 573.49 477.156"
      overflow="visible"
    >
      <switch>
        <foreignObject
          y="0"
          x="0"
          requiredExtensions="http://ns.adobe.com/AdobeIllustrator/10.0/"
          width="1"
          height="1"
        ></foreignObject>
        <g>
          <g id="&#x56FE;&#x5C42;_1">
            <polygon
              fillRule="evenodd"
              points="4.084 11.815 87.1 5.908 154.33 13.179 220.66 8.18 288.78 13.179 356.46 8.634 424.59 14.087 490.02 7.271 573.04 13.179 573.04 376.73 492.72 384.91 423.24 379.45 356.46 387.18 288.34 378.54 221.56 385.82 154.33 377.18 83.04 383.54 3.629 374.45"
              clipRule="evenodd"
              fill="#fff"
            />
            <polygon
              fillRule="evenodd"
              points="3.629 374.31 83.64 381.2 154.33 376.8 221.48 383.25 288.34 378.1 356.39 384.46 423.24 378.92 492.35 382.49 573.04 376.5 573.04 12.427 490.02 6.52 424.59 13.336 356.46 7.883 288.78 12.427 220.66 7.428 154.33 12.427 87.1 5.156 4.084 11.064"
              clipRule="evenodd"
              fill="#D5D4D4"
            />
            <polygon
              fillRule="evenodd"
              points="3.629 374.16 84.246 378.85 154.33 376.43 221.41 380.67 288.34 377.64 356.31 381.73 423.24 378.4 491.97 380.06 573.04 376.28 573.04 11.67 490.02 5.762 424.59 12.579 356.46 7.125 288.78 11.67 220.66 6.671 154.33 11.67 87.1 4.398 4.084 10.307"
              clipRule="evenodd"
              fill="#A8A8A8"
            />
            <polygon
              fillRule="evenodd"
              points="3.629 374 84.846 376.5 154.33 376.05 221.33 378.09 288.34 377.18 356.24 379 423.24 377.86 491.6 377.64 573.04 376.05 573.04 10.907 490.02 4.999 424.59 11.815 356.46 6.362 288.78 10.907 220.66 5.908 154.33 10.907 87.1 3.635 4.084 9.543"
              clipRule="evenodd"
              fill="#808080"
            />
            <polygon
              fillRule="evenodd"
              points="3.629 373.85 85.446 374.16 154.33 375.67 221.26 375.52 288.34 376.73 356.16 376.28 423.24 377.34 491.22 375.22 573.04 375.82 573.04 10.155 490.02 4.248 424.59 11.064 356.46 5.61 288.78 10.155 220.66 5.156 154.33 10.155 87.1 2.884 4.084 8.792"
              clipRule="evenodd"
              fill="#555554"
            />
            <polygon
              fillRule="evenodd"
              points="3.629 373.7 86.052 371.81 154.33 375.29 221.18 372.94 288.34 376.28 356.09 373.55 423.24 376.8 490.84 372.79 573.04 375.59 573.04 9.397 490.02 3.49 424.59 10.307 356.46 4.853 288.78 9.397 220.66 4.398 154.33 9.397 87.1 2.126 4.084 8.034"
              clipRule="evenodd"
              fill="#292929"
            />
            <polygon
              points="4.084 7.271 87.1 1.363 154.33 8.634 220.66 3.635 288.78 8.634 356.46 4.09 424.59 9.543 490.02 2.727 573.04 8.634 573.04 375.36 490.47 370.36 423.24 376.27 356.01 370.82 288.34 375.82 221.11 370.36 154.33 374.91 86.651 369.46 3.629 373.54"
              clipRule="evenodd"
              fillRule="evenodd"
            />
            <linearGradient
              id="XMLID_202_"
              y2="184.95"
              gradientUnits="userSpaceOnUse"
              y1="189.5"
              x2="500.21"
              x1="432.95"
            >
              <stop stopColor="#D9D8D8" offset="0" />
              <stop stopColor="#F1F0F0" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="422.62 6.816 422.62 374.45 490.33 368.55 490.33 1.363"
              clipRule="evenodd"
              fill="url(#XMLID_202_)"
            />
            <linearGradient
              id="XMLID_203_"
              y2="189.15"
              gradientUnits="userSpaceOnUse"
              y1="186.88"
              x2="493.98"
              x1="578.96"
            >
              <stop stopColor="#ABABAB" offset="0" />
              <stop stopColor="#D9D8D8" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="573.49 7.277 573.49 374.91 490.33 368.55 490.33 1.363"
              clipRule="evenodd"
              fill="url(#XMLID_203_)"
            />
            <linearGradient
              id="XMLID_204_"
              y2="187.91"
              gradientUnits="userSpaceOnUse"
              y1="187.91"
              x2="354"
              x1="432.62"
            >
              <stop stopColor="#C2C2C2" offset="0" />
              <stop stopColor="#A6A6A6" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="422.62 6.816 422.62 374.45 354.91 368.55 354.91 1.363"
              clipRule="evenodd"
              fill="url(#XMLID_204_)"
            />
            <linearGradient
              id="XMLID_205_"
              y2="187.46"
              gradientUnits="userSpaceOnUse"
              y1="187.91"
              x2="355.78"
              x1="284.88"
            >
              <stop stopColor="#E6E6E6" offset="0" />
              <stop stopColor="#EEEDED" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="286.75 374 354.91 368.55 354.91 1.363 286.5 6.229"
              clipRule="evenodd"
              fill="url(#XMLID_205_)"
            />
            <linearGradient
              id="XMLID_206_"
              y2="186.1"
              gradientUnits="userSpaceOnUse"
              y1="188.83"
              x2="212.84"
              x1="281.92"
            >
              <stop stopColor="#C9C9C8" offset="0" />
              <stop stopColor="#A6A6A6" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="286.75 6.816 286.75 374 219.04 368.55 219.04 1.363"
              clipRule="evenodd"
              fill="url(#XMLID_206_)"
            />
            <linearGradient
              id="XMLID_207_"
              y2="187.22"
              gradientUnits="userSpaceOnUse"
              y1="187.68"
              x2="219.95"
              x1="152.23"
            >
              <stop stopColor="#E6E6E6" offset="0" />
              <stop stopColor="#F3F3F3" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="151.78 373.54 219.04 368.55 219.03 1.363 151.78 5.453"
              clipRule="evenodd"
              fill="url(#XMLID_207_)"
            />
            <linearGradient
              id="XMLID_208_"
              y2="184.32"
              gradientUnits="userSpaceOnUse"
              y1="188.41"
              x2="75.414"
              x1="145.85"
            >
              <stop stopColor="#ccc" offset="0" />
              <stop stopColor="#A6A6A6" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="151.78 5.459 151.78 373.54 84.07 367.64 83.616 0"
              clipRule="evenodd"
              fill="url(#XMLID_208_)"
            />
            <linearGradient
              id="XMLID_209_"
              y2="187.79"
              gradientUnits="userSpaceOnUse"
              y1="185.52"
              x2="80.463"
              x1="-5.4438"
            >
              <stop stopColor="#F1F0F0" offset="0" />
              <stop stopColor="#E8E7E8" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="0 5.914 0 373.54 84.064 367.64 84.064 0.006"
              clipRule="evenodd"
              fill="url(#XMLID_209_)"
            />
            <linearGradient
              id="XMLID_210_"
              y2="188.81"
              gradientUnits="userSpaceOnUse"
              y1="187"
              x2="81.52"
              x1="17.899"
            >
              <stop stopColor="#D5BA9C" offset="0" />
              <stop stopColor="#B39570" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="16.354 20.922 16.354 360.37 84.064 353.1 84.064 15.469"
              clipRule="evenodd"
              fill="url(#XMLID_210_)"
            />
            <linearGradient
              id="XMLID_211_"
              y2="188.38"
              gradientUnits="userSpaceOnUse"
              y1="187.47"
              x2="151.27"
              x1="87.651"
            >
              <stop stopColor="#9E8263" offset="0" />
              <stop stopColor="#957857" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="84.07 15.451 84.07 353.08 151.78 360.35 151.78 20.904"
              clipRule="evenodd"
              fill="url(#XMLID_211_)"
            />
            <linearGradient
              id="XMLID_212_"
              y2="187.92"
              gradientUnits="userSpaceOnUse"
              y1="187.92"
              x2="219.95"
              x1="153.14"
            >
              <stop stopColor="#A78A66" offset="0" />
              <stop stopColor="#D0B594" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="151.78 20.922 151.78 360.37 219.49 356.28 219.49 15.469"
              clipRule="evenodd"
              fill="url(#XMLID_212_)"
            />
            <linearGradient
              id="XMLID_213_"
              y2="189.51"
              gradientUnits="userSpaceOnUse"
              y1="188.61"
              x2="286.68"
              x1="223.06"
            >
              <stop stopColor="#A78A66" offset="0" />
              <stop stopColor="#957857" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="219.49 15.451 219.49 356.26 287.2 362.63 287.2 20.904"
              clipRule="evenodd"
              fill="url(#XMLID_213_)"
            />
            <linearGradient
              id="XMLID_214_"
              y2="189.05"
              gradientUnits="userSpaceOnUse"
              y1="188.6"
              x2="354.66"
              x1="286.95"
            >
              <stop stopColor="#A78A66" offset="0" />
              <stop stopColor="#D5BA9C" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="287.2 20.922 287.2 362.18 354.91 354 354.91 15.469"
              clipRule="evenodd"
              fill="url(#XMLID_214_)"
            />
            <linearGradient
              id="XMLID_215_"
              y2="188.38"
              gradientUnits="userSpaceOnUse"
              y1="187.47"
              x2="422.11"
              x1="358.49"
            >
              <stop stopColor="#A78A66" offset="0" />
              <stop stopColor="#957857" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="354.91 15.451 354.91 353.99 422.62 360.35 422.62 20.904"
              clipRule="evenodd"
              fill="url(#XMLID_215_)"
            />
            <linearGradient
              id="XMLID_216_"
              y2="187.92"
              gradientUnits="userSpaceOnUse"
              y1="187.92"
              x2="490.79"
              x1="423.53"
            >
              <stop stopColor="#A1825E" offset="0" />
              <stop stopColor="#CFB48F" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="422.62 20.922 422.62 360.37 490.33 354 490.33 15.469"
              clipRule="evenodd"
              fill="url(#XMLID_216_)"
            />
            <linearGradient
              id="XMLID_217_"
              y2="188.38"
              gradientUnits="userSpaceOnUse"
              y1="187.47"
              x2="557.53"
              x1="493.91"
            >
              <stop stopColor="#A78A66" offset="0" />
              <stop stopColor="#957857" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="490.33 15.451 490.33 353.99 558.04 360.35 558.04 20.904"
              clipRule="evenodd"
              fill="url(#XMLID_217_)"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m333.59 186.75l-4.065-0.679-9.489-2.036-6.779 2.714-14.912-6.102-3.387-6.101c-5.32 0.442-9.876-0.054-17.625 3.387l-5.423-2.029c-4.532 3.629-6.447 7.889-8.132 12.203-5.283 4.205-9.251 9.725-12.881 15.59l1.358 6.774-0.679 4.066c0.266 5.15 3.054 8.113 6.101 10.852l9.489 8.131 10.167 0.679c2.259-1.806 6.78-5.422 12.203-0.679 5.423 4.745 5.423-3.387 6.78 2.709 1.351 6.101 0 6.101-0.679 6.101 0.679 2.037 2.029 6.101 5.423 9.495 3.387 3.387 4.065 5.417 2.708 8.131-1.356 2.708-3.387 4.065-3.387 7.452 0 3.393 0.679 4.75 2.03 8.138 1.356 3.386 0.679 3.386 2.035 8.131 1.357 4.75 3.388 8.138 4.745 11.524 1.357 3.394-0.679 5.424 2.036 6.78 2.708 1.357 7.452 2.03 10.166 1.357 2.709-0.68 10.168-5.423 12.876-10.846 2.715-5.423 1.812-6.326 1.357-6.78 1.582-0.902 4.745-2.708 3.387-8.811-1.351-6.102 5.423-6.102 6.78-6.102s4.745-6.101 2.715-9.488c-2.036-3.394-2.715-16.947 5.423-20.334 8.131-3.393 11.519-16.948 12.197-18.305 0.679-1.357-6.55 0-10.168 0.679l-2.708-2.036c-0.455-1.351-1.357-4.065-4.071-4.744-2.709-0.679-3.163-3.388-3.388-4.745 0-0.679 0-2.036-2.708-3.387-2.714-1.357 0-6.102-2.036-6.781s-6.78-10.845-6.102-12.875c0.679-2.036-0.679-2.036-0.679-2.714s-0.45 0.23-0.68 0.68z"
              fill="#442E12"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m168.88 350.79c-1.357-2.715-6.78 0-4.066-8.138 2.709-8.132-0.679-4.065 2.03-7.452 2.714-3.395 0-2.037-0.679-4.751-0.679-2.708-0.679-3.387 1.357-4.065 2.035-0.679 2.035-3.388 2.714-4.066 0.679-0.679-0.23-4.289-0.679-6.102 1.582 0.679 4.745 2.036 4.745-2.708s2.708 2.029 6.774-2.035c4.072-4.066 2.714-6.103 2.036-6.781-0.679-0.678-0.679-1.127-0.679-1.351h5.423l2.036-2.036c2.259-2.484 6.774-7.459 7.453-10.168 0.678-2.708-0.679-3.387 1.356-5.422 2.037-2.036 3.394-4.066 6.102-4.744 2.709-0.68 3.387 0.678 5.423-2.037 2.036-2.707 4.744-8.809 4.065-10.845-0.679-2.03 0-4.744 0.679-5.423 0.679-0.672 10.846-10.167 2.708-12.876-8.131-2.714 0.679-3.392-7.452-3.392s-4.745 0.678-5.423-0.673c-0.678-1.356-3.387-3.393-4.745-3.393-1.357 0-4.065-1.351-5.422-2.03l1.356-2.035c-1.812-2.26-5.423-6.78-12.203-6.78s-3.386-2.708-4.065-3.387c-0.679-0.679-2.715-2.036-3.394-2.709-0.678-0.679-3.387-2.036-4.744-2.714-0.903 0.679-2.708 2.036-3.387 1.357-0.678-0.678-2.714 0-3.387-0.678-0.679-0.679-3.393-0.679-4.071-0.679-0.673 0-2.03-0.679-2.708-0.679-0.679 0-3.394-0.679-4.065 0-0.679 0.679-2.715 1.357-2.715 3.393 0 2.03-0.679 1.352-1.357 2.03-0.672 0.679-1.351 1.357-1.351 2.036 0 0.678 0.679 2.029 0.679 2.708v2.036s0 2.709-0.679 2.709h-1.357l-0.679 1.357-0.679 1.357h-0.678c-0.448 0.224-1.351 0.678-0.672 2.029 0.672 1.357 0 2.036-0.679 2.036s0.679 1.357 1.351 2.036c-0.672 0.673-2.03 2.03-2.03 4.065 0 2.037 2.708 6.102 3.387 6.781 0.679 0.673 2.036 2.029 2.715 4.066 0.672 2.029 3.387 6.773 6.095 8.131 2.715 1.357 6.781 4.744 6.102 9.488-0.679 4.751-1.127 9.265-1.352 11.524-0.23 0.23-0.679 0.678-1.357 4.065-0.678 3.394 0.679 4.751-0.678 7.459-1.357 2.709-2.715 8.811-2.715 11.524 0 2.708-1.351 4.065-0.679 5.423 0.679 1.357 0.679 6.78 0 9.489-0.672 2.708-0.672 10.846 0 13.554 0.679 2.714-0.672 6.101 3.394 8.816 4.07 2.7 8.59 1.35 10.85 0.67z"
              fill="#442E12"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m139.73 227.42l-4.744-3.388c-0.225-1.581-0.679-4.744-2.709-5.422-2.036-0.679-4.744-1.133-6.101-1.357-0.225-1.582-0.679-4.745-4.744-4.066-4.066 0.679-3.387 2.03-8.81 0-5.423-2.036-9.495-5.422-10.167-6.101-0.679-0.679-1.357-2.036-2.035-4.065-0.679-2.037-7.459-12.203-8.811-14.233-1.357-2.036-7.458-4.071-12.203-7.459-4.744-3.393-10.167-16.269-9.489-18.977 0.673-2.714-4.065-24.407-9.495-30.508-5.417-6.096-3.387-8.81-8.81-10.846-5.422-2.029-8.131-6.774-12.197-7.453-4.071-0.678-6.78-5.422-11.524-4.744s-7.907 6.102-9.488 8.81l-0.679-48.127c3.611 0.23 10.846 0.678 14.912 2.714 4.065 2.029 9.488 5.423 13.554 6.773 4.065 1.358 2.714 2.037 6.101 0 3.387-2.029 12.203-5.422 12.882-5.422s1.351 2.036 4.744 2.036c3.388 0 4.745 0 8.131 2.036 3.394 2.029 6.102 2.708 7.459 4.744 1.351 2.03 4.744 2.708 5.423 2.03 0.678-0.679 4.065 0 6.095 2.035 2.036 2.036-0.673-3.387 2.036-4.065 2.714-0.678 4.066-3.394 7.459 0 3.387 3.387 6.774 2.03 9.488 2.03 2.708 0 3.387-0.679 4.745-4.066 1.357-3.387 1.357-5.423-0.679-6.78-2.03-1.351-4.066-1.351-2.708-4.744 1.351-3.388 0-9.489 3.387-4.745 3.387 4.745 3.387 4.745 3.387 7.459v2.708c0 0.679 3.393 4.072 4.744 6.102 1.357 2.036 2.036 2.036 3.393 4.744 1.352 2.714 4.066 0.679 4.066-4.744s-4.745-9.489 0-6.78c4.744 2.714 6.102 8.138 5.423 10.846-0.679 2.714-1.357 5.423-3.387 6.779-2.036 1.358-4.75 2.715-5.423 4.745-0.679 2.035-3.393 4.744-5.422 6.101-2.036 1.357-4.066 5.423-5.423 6.78-1.357 1.351-0.678 6.774-0.678 8.81s2.708 4.744 5.423 6.102c2.708 1.357 4.065 0.678 6.774 2.708 2.714 2.036 5.423 4.745 6.102 5.423s-0.679 5.423 1.357 6.78c2.036 1.358 6.102-1.357 6.102-4.744 0-3.393 2.036-8.131 2.708-8.131 0.679 0 4.744-2.714 0-5.423-4.744-2.714-4.066-8.137-2.708-8.816 1.357-0.679-3.387-1.357 2.036-4.065 5.417-2.714 4.066-2.714 8.81 2.035 4.744 4.745-1.357 6.096 5.422 8.81 6.781 2.708 6.102-2.036 6.781-2.714 0.679-0.672 4.065 4.072 5.417 8.138 1.357 4.065 4.75 2.036 6.78 6.774 2.036 4.75 3.393 6.102 2.036 9.495-1.357 3.387-3.394 2.029-6.78 2.708-3.387 0.679-7.453 0.679-10.167 2.714-2.709 2.036 2.035 2.708 3.393 5.423 1.351 2.708 4.065 4.066-0.679 5.423-4.75 1.357-4.75-0.678-6.781 2.03-2.035 2.714-4.065 2.036-3.387 5.423 0.673 3.393-3.393 0-5.429 3.393-2.029 3.387-2.708 4.066-2.708 8.131s-2.708 4.745-4.065 6.78c-1.358 2.03-5.423 7.452-4.745 8.81 0.679 1.356 0.679 4.744 0.679 6.101s-2.715-3.617-4.072-6.101c-0.448-0.904-1.351-2.708-4.065-3.387-2.709-0.679-2.036-0.679-5.423 0s-4.744-0.679-4.065 2.029c0.679 2.714-3.387 0-6.102 0.679-2.708 0.679-2.708-1.351-4.066 4.072-1.357 5.417-3.387 5.417-1.357 8.81 2.036 3.387 3.393 7.452 6.102 7.452 2.714 0 2.714 1.357 4.744-1.351 2.036-2.714-0.679-3.393 3.387-4.071 4.071-0.673 6.102-0.673 4.071 2.036-2.036 2.708-2.036 4.065-2.036 5.422s0.678 2.03 2.036 2.03c1.352 0 2.03-1.352 4.744 0 2.709 1.357 1.352 1.357 1.352 4.072 0 2.708-1.352 3.387 0 4.744 1.357 1.357 3.393 2.708 4.75 2.708 1.36 0.03-2.25 0.94-4.06 1.38z"
              fill="#442E12"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m338.34 190.82c1.357 1.581 2.715 2.035 4.072 4.744 1.352 2.714 1.352 2.036 3.387 6.101 2.036 4.066 1.357 4.066 2.715 6.102 1.352 2.029 2.709 3.387 3.387 4.744 0.679 1.357 2.036 4.521 2.715 6.102 0.673 0 2.029 0 4.738-0.679 2.714-0.678 4.072-2.714 6.102-2.714 2.036 0 3.393-2.709 4.071-2.709 0.673 0 2.029-1.357 4.066-2.036 2.029-0.679 4.065-2.708 4.065-3.387s2.036-3.387 2.036-4.745-2.036-5.423-3.394-5.423-4.745 2.036-5.423 2.708c-0.679 0.678-2.933 0.23-4.065 0-0.903-1.805-2.708-5.417-3.387-6.095-0.68-0.68 2.933-4.521 2.708-6.102-0.449 0.903-1.357 2.708 1.357 4.744 2.708 2.037 4.064 2.037 5.423 2.037 1.356 0 3.387 0 4.744 1.351 1.357 1.357 3.387 1.357 5.423 1.357 2.029 0 4.064-0.678 6.102 0 2.03 0.679 4.744-0.678 5.423 1.357 0.679 2.029 4.065 4.745 4.744 5.423s2.708-1.357 3.387 0c0.679 1.352 1.357 2.03 1.357 4.745 0 2.708 1.358 5.422 2.036 7.453 0.673 2.036 3.387 4.744 3.387 6.78s-0.678 4.745 2.036 4.745c2.708 0 3.387-2.709 4.065-4.066 0.679-1.357 0.679-4.975 0.679-6.78 0.679-1.127 2.03-3.388 4.066-4.745 2.029-1.357 1.356-3.387 4.064-4.744 2.715-1.357 0.679-3.387 5.424-3.387 4.744 0 3.387-4.072 6.101-0.678 2.709 3.387 4.745 5.423 4.745 7.452 0 2.036-2.036 4.066 0 4.066s0.678-2.03 3.387-2.03c2.715 0 2.715 0 2.715 2.709 0 2.714-1.357 2.714 0 5.423 1.356 2.714 0.679 1.357 2.709-0.672 2.034-2.036 2.714-1.357 4.064 0 1.357 1.351 2.036 0.672 3.394 3.387 1.357 2.708 0.679 4.745 4.066 1.357 3.387-3.393 4.064-0.679 4.064-4.744 0-4.072 1.357-5.429-2.029-6.102-3.394-0.679-3.394-2.036-4.072-4.072-0.672-2.029-1.351-6.095 2.716-6.095 4.064 0 8.81 0.678 12.202-0.678 3.387-1.358 6.102-3.394 6.774-4.745 0.678-1.357 4.296-5.877 6.102-8.138l-1.351-8.131-3.395-4.072c-0.679 0.231-2.029 0.679 1.357-1.351 3.388-2.036 6.78-3.394 3.388-3.394-3.388 0-5.424 0.679-5.424 0 0-0.673-3.387-2.029-3.387-2.708s7.46-2.714 8.132-3.387c0.679-0.679 4.744 0 5.423 1.351 0.679 1.357 2.036 2.037 2.715 4.745 0.679 2.715-0.679 2.715 0 4.744 0.679 2.037 2.708 0.679 3.387 0 0.679-0.672 2.715-2.029 2.036-3.387s-2.036-4.065-2.715-5.423c0-0.902 0-2.708 0.679-3.387s2.715-2.036 3.387-2.714c0.679-0.672-1.351-3.387 3.394-2.709 4.744 0.679 5.423-0.678 7.453-2.708 2.035-2.036 3.393-6.102 4.75-7.459 1.352-1.356 1.352-3.387 1.352-6.102 0-2.714 1.356-6.78 0.678-7.458-0.678-0.672-3.387-3.387-4.744-3.387-1.356 0-3.617-2.26-4.744-3.387 1.127-0.903 3.388-2.714 4.065-3.393 0.679-0.672-2.029-4.744 3.394-7.452 5.417-2.715 4.065-4.745 10.168-4.066 6.094 0.672 5.422 0.672 9.488 0.672 4.064 0 5.422-1.351 7.452-3.387 2.035-2.036 5.423-9.489 8.138-5.423 2.708 4.066 4.065 1.358 4.744 0-0.225-9.943-0.679-29.823-0.679-30.501s-3.617-2.484-5.423-3.387c0-1.357 0-4.072-6.102-4.072s-8.811 2.036-9.488-0.673c-0.68-2.715-0.68-2.036-2.037-3.393-1.351-1.357-7.451-3.387-8.81-4.065-1.357-0.679-5.423-4.745-6.101-1.357-0.679 3.393-2.485 7.004-3.388 8.816-2.484-1.133-7.459-3.393-8.132-3.393-0.679 0-2.938 2.26-4.07 3.393l-4.745-3.393 1.357-4.744c-2.939-2.26-8.811-6.78-9.489-6.78s4.066 3.393-2.714 3.393c-6.774 0-10.167-1.356-11.524-1.356-1.352 0-8.132 1.356-8.811 1.356s-4.743 0.449-6.78 0.679c1.582-1.357 4.745-4.072 5.423-4.75 0.679-0.672 4.065-6.774 4.744-6.774 0.68 0 2.037-4.745 0.68-7.459-1.358-2.708-4.745-6.101-6.774-5.422-2.035 0.679-3.842-0.224-4.751-0.679-0.448-1.127-1.351-3.387-1.351-4.065s-2.036-4.744-3.394-1.357c-1.351 3.393-4.743 7.459-6.096 10.173-1.356 2.708-12.881 0.673-16.268 5.417-3.395 4.744-8.138 8.138-8.138 10.167 0 2.036-6.774 0-6.102 4.071 0.679 4.066 2.715 8.132 2.036 8.81-0.68 0.679-2.709-3.387-5.424-2.708-2.708 0.673-7.458 1.351-6.779 6.096 0.679 4.744-3.388 6.101-5.424 6.78-0.672-3.163-2.029-9.489-1.351-10.168 0.679-0.678 8.132-9.488 1.351-7.458-6.773 2.036-11.518 2.715-9.488 9.495 2.036 6.773 2.036 10.846 2.715 12.875 0.679 2.036-6.78-2.708-7.459-3.387-0.68-0.679-6.78-6.78-6.78-2.715 0 4.072 0 6.781-1.351 6.781h-10.168c-0.679 0 0.673-2.037-4.071 1.357-4.745 3.387-6.102 5.423-6.774 6.774-0.679 1.357-1.581-2.709-2.036-4.744-1.126 1.357-3.387 4.071-2.708 6.101 0.679 2.036-1.582 1.582-2.714 1.357-2.036-0.455-6.103-1.357-4.745 1.357 1.357 2.709 2.036 3.388 2.036 4.744 0 1.358-3.163-0.454-4.75-1.356 0 1.356 0 4.065-0.673 4.065-0.679 0-3.842-3.163-5.423-4.744l-2.036-8.132 8.81 3.387 4.072-1.356c0.673-1.352 2.03-4.066 0-5.423-2.036-1.352-6.78-4.066-7.459-4.745-0.678-0.679 0-2.708-4.065-3.387-4.065-0.678-8.137-1.357-8.137-2.714 0-1.351-6.774-4.065-8.812-2.708-2.035 1.357-8.131 3.387-9.487 6.78-1.357 3.387-5.424 6.774-6.781 10.167-1.356 3.387-4.743 4.745-4.743 8.811 0 4.065-2.709 6.101-4.066 7.458-1.357 1.351-6.102 0.673-6.102 5.422 0 4.745-0.679 6.096 0 8.132s4.744 3.387 6.102 2.714c1.357-0.678 8.81-6.78 8.81-1.357 0 5.422-2.035 8.131 0 8.131 2.036 0 3.387-4.744 4.745-7.452 1.357-2.715 2.715-2.036 2.715-4.072 0-2.03-2.715-2.708-2.715-5.423 0-2.708-0.679-5.422 0-6.095 0.679-0.679 3.393-3.393 5.423-4.072 2.036-0.672 1.356-2.708 2.714-5.422 1.352-2.708 0.679-3.388 4.065-2.708 3.388 0.679 2.709 1.357 2.037 4.745-0.679 3.386-2.716 4.065-4.751 6.78-2.029 2.708-3.387 6.101-1.352 8.131 2.03 2.036 5.424 1.357 6.774 0.679 1.357-0.679 2.715-2.036 4.071-1.357 1.353 0.679 3.388 4.072 2.03 4.744-1.356 0.679-6.773 1.357-8.811 1.357-2.035 0 0.679 3.393 0.679 5.423 0 2.036-0.454 2.484-0.679 2.714-1.581-0.23-4.743-0.679-4.743 0.679 0 1.351-2.715 5.417-3.394 6.096-0.673 0.678-3.387 0-5.424 0.678-2.029 0.679-8.81-0.678-9.488-0.678s-6.102 2.714-6.102 3.393-4.064 1.351-4.064 3.387c0 2.036-4.066 4.066-4.066 4.745 0 0.678-2.037 0.678-3.388 2.714-1.356 2.03-2.035 5.423-2.035 6.774 0 1.357-0.454 5.423-0.678 7.458-1.582-0.224-4.745-0.678-6.102-0.678-1.351 0-8.81-3.388-7.453 0.678 1.358 4.066 4.066 14.233 4.745 14.233s6.78-1.357 8.81-1.357c2.036 0 4.065-3.387 5.422-5.422 1.358-2.03 1.358-6.096 6.102-7.454 4.744-1.356 6.102-2.708 8.132-0.678 2.036 2.036 10.166 6.101 10.166 6.78s0.68 4.066-0.671 4.745c-1.358 0.678-4.751-1.358-4.072 0.678 0.679 2.03 0.679 3.388 2.035 3.388 1.357 0 2.037-4.065 4.066-4.744 2.036-0.679 5.423-2.037 2.715-4.066-2.715-2.036-6.103-4.065-7.46-5.423-1.356-1.356-1.356-7.458-0.678-7.458s2.035 3.393 4.064 5.423c2.037 2.035 4.073 2.035 5.424 4.065 1.357 2.036-1.351-0.672 1.357 4.072 2.714 4.744 5.423 6.095 6.101 6.095 0.679 0-1.356-5.422-0.678-6.774 0.678-1.357 4.744-4.065 5.423-0.679s-1.357 4.745 1.357 6.78c2.709 2.03 0 2.709 6.102 2.709 6.095 0 7.452-0.679 8.132 0 0.678 0.679 0-1.357 0.678 2.036 0.679 3.387 0.679 5.423 0 6.774-0.678 1.357-3.387 0.679-3.387 1.357-0.02 0.73 0.43 3.89 0.66 5.47z"
              fill="#442E12"
            />
            <linearGradient
              id="XMLID_218_"
              y2="239.5"
              gradientUnits="userSpaceOnUse"
              y1="238.59"
              x2="557.77"
              x1="13.807"
            >
              <stop stopColor="#694C2B" offset="0" />
              <stop stopColor="#89663D" offset=".12" />
              <stop stopColor="#563B19" offset=".25" />
              <stop stopColor="#AB8559" offset=".37" />
              <stop stopColor="#443017" offset=".5" />
              <stop stopColor="#9E7A52" offset=".62" />
              <stop stopColor="#664A29" offset=".75" />
              <stop stopColor="#9C784F" offset=".87" />
              <stop stopColor="#694C2B" offset="1" />
            </linearGradient>
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m311.23 301.99c-2.715-1.356-0.679-3.387-2.036-6.78-1.357-3.387-3.388-6.774-4.745-11.524-1.356-4.745-0.679-4.745-2.035-8.131-1.352-3.388-2.03-4.745-2.03-8.138 0-3.387 2.03-4.744 3.387-7.452 1.357-2.714 0.679-4.745-2.708-8.131-3.394-3.394-4.744-7.458-5.423-9.495 0.679 0 2.029 0 0.679-6.101-1.357-6.096-1.357 2.036-6.78-2.709-0.8-0.697-1.575-1.217-2.333-1.582v-56.84c2.715-0.346 5.145-0.334 7.756-0.552l3.387 6.101 14.912 6.102 6.779-2.714 9.489 2.036 4.065 0.679c0.23-0.454 0.679-1.357 0.679-0.679s1.357 0.679 0.679 2.714c-0.679 2.03 4.065 12.197 6.102 12.875s-0.678 5.423 2.036 6.781c2.708 1.351 2.708 2.708 2.708 3.387 0.225 1.357 0.679 4.066 3.388 4.745 2.714 0.679 3.616 3.393 4.071 4.744l1.66 1.249v17.94c-0.321 0.171-0.648 0.328-0.981 0.474-8.138 3.387-7.459 16.94-5.423 20.334 2.03 3.387-1.357 9.488-2.715 9.488s-8.131 0-6.78 6.102c1.358 6.103-1.805 7.908-3.387 8.811 0.454 0.454 1.357 1.357-1.357 6.78-2.708 5.423-10.167 10.166-12.876 10.846-2.73 0.66-7.47-0.02-10.18-1.37z"
              fill="url(#XMLID_218_)"
            />
            <linearGradient
              id="XMLID_219_"
              y2="287.38"
              gradientUnits="userSpaceOnUse"
              y1="286.47"
              x2="557.69"
              x1="13.727"
            >
              <stop stopColor="#694C2B" offset="0" />
              <stop stopColor="#89663D" offset=".12" />
              <stop stopColor="#563B19" offset=".25" />
              <stop stopColor="#AB8559" offset=".37" />
              <stop stopColor="#443017" offset=".5" />
              <stop stopColor="#9E7A52" offset=".62" />
              <stop stopColor="#664A29" offset=".75" />
              <stop stopColor="#9C784F" offset=".87" />
              <stop stopColor="#694C2B" offset="1" />
            </linearGradient>
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m158.03 350.12c-4.065-2.716-2.715-6.103-3.394-8.816-0.672-2.708-0.672-10.846 0-13.554 0.679-2.709 0.679-8.132 0-9.489-0.672-1.357 0.679-2.715 0.679-5.423 0-2.714 1.357-8.815 2.715-11.524 1.357-2.708 0-4.065 0.678-7.459 0.679-3.388 1.127-3.835 1.357-4.065 0.224-2.26 0.673-6.773 1.352-11.524 0.679-4.744-3.387-8.131-6.102-9.488-1.242-0.625-2.49-1.964-3.539-3.424v-41.57c0.649-0.878 1.727-1.339 2.188-1.8 0.672-0.679 3.387 0 4.065 0s2.036 0.679 2.708 0.679c0.679 0 3.393 0 4.071 0.679 0.673 0.678 2.709 0 3.387 0.678 0.679 0.679 2.484-0.678 3.387-1.357 1.357 0.679 4.066 2.036 4.744 2.714 0.679 0.673 2.714 2.03 3.394 2.709 0.679 0.679-2.715 3.387 4.065 3.387s10.392 4.521 12.203 6.78l-1.356 2.035c1.356 0.68 4.065 2.03 5.422 2.03 1.358 0 4.066 2.037 4.745 3.393 0.679 1.352-2.708 0.673 5.423 0.673s-0.679 0.678 7.452 3.392c0.758 0.249 1.352 0.558 1.813 0.915v6.526c-1.624 2.702-4.193 5.108-4.521 5.435-0.679 0.679-1.357 3.393-0.679 5.423 0.679 2.037-2.029 8.138-4.065 10.845-2.037 2.715-2.714 1.357-5.423 2.037-2.708 0.678-4.065 2.708-6.102 4.744-2.035 2.035-0.678 2.714-1.356 5.422-0.679 2.709-5.194 7.684-7.453 10.168l-2.036 2.036h-5.423c0 0.224 0 0.673 0.679 1.351 0.678 0.679 2.036 2.715-2.036 6.781-4.065 4.064-6.774-2.709-6.774 2.035s-3.163 3.387-4.745 2.708c0.449 1.813 1.358 5.423 0.679 6.102s-0.679 3.388-2.714 4.066c-2.036 0.679-2.036 1.357-1.357 4.065 0.679 2.714 3.393 1.356 0.679 4.751-2.709 3.387 0.679-0.68-2.03 7.452-2.714 8.138 2.709 5.423 4.066 8.138-2.27 0.69-6.79 2.04-10.86-0.66z"
              fill="url(#XMLID_219_)"
            />
            <linearGradient
              id="XMLID_220_"
              y2="127.67"
              gradientUnits="userSpaceOnUse"
              y1="126.76"
              x2="557.95"
              x1="13.997"
            >
              <stop stopColor="#694C2B" offset="0" />
              <stop stopColor="#89663D" offset=".12" />
              <stop stopColor="#563B19" offset=".25" />
              <stop stopColor="#AB8559" offset=".37" />
              <stop stopColor="#443017" offset=".5" />
              <stop stopColor="#9E7A52" offset=".62" />
              <stop stopColor="#664A29" offset=".75" />
              <stop stopColor="#9C784F" offset=".87" />
              <stop stopColor="#694C2B" offset="1" />
            </linearGradient>
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m79.405 181.33c-4.744-3.393-10.167-16.269-9.489-18.977 0.673-2.714-4.065-24.407-9.495-30.508-5.417-6.096-3.387-8.81-8.81-10.846-5.422-2.029-8.131-6.774-12.197-7.453-4.071-0.678-6.78-5.422-11.524-4.744s-7.907 6.102-9.488 8.81l-0.679-48.127c3.611 0.23 10.846 0.678 14.912 2.714 4.065 2.029 9.488 5.423 13.554 6.773 4.065 1.358 2.714 2.037 6.101 0 3.387-2.029 12.203-5.422 12.882-5.422s1.351 2.036 4.744 2.036c3.388 0 4.745 0 8.131 2.036 2.442 1.466 4.533 2.224 6.023 3.296v103.24c-1.563-0.84-3.181-1.77-4.665-2.83zm72.375-4.55v-52.454c0.424-0.66 0.594-1.496 0-2.399v-15.766c2.563-1.23 2.466-0.467 6.253 3.326 4.744 4.745-1.357 6.096 5.422 8.81 6.781 2.708 6.102-2.036 6.781-2.714 0.679-0.672 4.065 4.072 5.417 8.138 1.357 4.065 4.75 2.036 6.78 6.774 2.036 4.75 3.393 6.102 2.036 9.495-1.357 3.387-3.394 2.029-6.78 2.708-3.387 0.679-7.453 0.679-10.167 2.714-2.709 2.036 2.035 2.708 3.393 5.423 1.351 2.708 4.065 4.066-0.679 5.423-4.75 1.357-4.75-0.678-6.781 2.03-2.035 2.714-4.065 2.036-3.387 5.423 0.673 3.393-3.393 0-5.429 3.393-2.029 3.387-2.708 4.066-2.708 8.131 0.01 0.58-0.04 1.09-0.14 1.55z"
              fill="url(#XMLID_220_)"
            />
            <linearGradient
              id="XMLID_221_"
              y2="126.14"
              gradientUnits="userSpaceOnUse"
              y1="125.23"
              x2="557.96"
              x1="13.997"
            >
              <stop stopColor="#694C2B" offset="0" />
              <stop stopColor="#89663D" offset=".12" />
              <stop stopColor="#563B19" offset=".25" />
              <stop stopColor="#AB8559" offset=".37" />
              <stop stopColor="#443017" offset=".5" />
              <stop stopColor="#9E7A52" offset=".62" />
              <stop stopColor="#664A29" offset=".75" />
              <stop stopColor="#9C784F" offset=".87" />
              <stop stopColor="#694C2B" offset="1" />
            </linearGradient>
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m456.96 223.36c-1.357-2.714-2.036-2.036-3.394-3.387-1.351-1.357-2.03-2.036-4.064 0-2.03 2.029-1.353 3.387-2.709 0.672-1.357-2.708 0-2.708 0-5.423 0-2.709 0-2.709-2.715-2.709-2.709 0-1.351 2.03-3.387 2.03s0-2.03 0-4.066c0-2.029-2.036-4.065-4.745-7.452-2.714-3.394-1.356 0.678-6.101 0.678s-2.709 2.03-5.424 3.387c-0.896 0.448-1.423 0.969-1.799 1.521v-155.13c0.666-0.158 1.121-0.412 1.121-0.945 0-2.03 4.743-5.423 8.138-10.167 3.387-4.744 14.911-2.708 16.268-5.417 1.353-2.714 4.745-6.78 6.096-10.173 1.357-3.387 3.394 0.679 3.394 1.357 0 0.679 0.902 2.938 1.351 4.065 0.909 0.455 2.716 1.357 4.751 0.679 2.029-0.679 5.416 2.714 6.774 5.422 1.357 2.715 0 7.459-0.68 7.459-0.679 0-4.065 6.102-4.744 6.774-0.678 0.679-3.841 3.393-5.423 4.75 2.037-0.23 6.102-0.679 6.78-0.679s7.459-1.356 8.811-1.356c1.357 0 4.75 1.356 11.524 1.356 2.218 0 3.205-0.363 3.545-0.854v111.74c-1.473-0.672-3.732-0.891-4.224-0.4-0.672 0.673-8.132 2.708-8.132 3.387s3.387 2.035 3.387 2.708c0 0.679 2.036 0 5.424 0 3.393 0 0 1.357-3.388 3.394-3.387 2.029-2.036 1.582-1.357 1.351l3.395 4.072 1.351 8.131c-1.806 2.26-5.424 6.78-6.102 8.138-0.673 1.351-3.388 3.387-6.774 4.745-3.393 1.356-8.138 0.678-12.202 0.678-4.066 0-3.388 4.066-2.716 6.095 0.679 2.036 0.679 3.393 4.072 4.072 3.387 0.673 2.029 2.03 2.029 6.102 0 4.065-0.678 1.351-4.064 4.744-3.38 3.39-2.7 1.36-4.06-1.35zm-105.06-10.85c-0.678-1.357-2.035-2.714-3.387-4.744-1.357-2.036-0.679-2.036-2.715-6.102-2.035-4.065-2.035-3.387-3.387-6.101-1.357-2.709-2.715-3.163-4.072-4.744-0.225-1.582-0.672-4.745-0.672-5.423s2.709 0 3.387-1.357c0.679-1.352 0.679-3.388 0-6.774-0.678-3.393 0-1.357-0.678-2.036-0.68-0.679-2.037 0-8.132 0-6.102 0-3.393-0.679-6.102-2.709-2.715-2.035-0.679-3.393-1.357-6.78s-4.745-0.679-5.423 0.679c-0.679 1.352 1.356 6.774 0.678 6.774s-3.387-1.352-6.101-6.095c-2.708-4.744 0-2.036-1.357-4.072-1.351-2.03-3.387-2.03-5.424-4.065-2.029-2.03-3.387-5.423-4.064-5.423-0.679 0-0.679 6.102 0.678 7.458 1.357 1.358 4.745 3.388 7.46 5.423 2.708 2.029-0.679 3.387-2.715 4.066-2.029 0.679-2.709 4.744-4.066 4.744-1.356 0-1.356-1.357-2.035-3.388-0.679-2.036 2.714 0 4.072-0.678 1.351-0.679 0.671-4.066 0.671-4.745s-8.13-4.744-10.166-6.78c-2.03-2.03-3.388-0.679-8.132 0.678-0.63 0.182-1.175 0.406-1.654 0.673v-21.359c0.188-0.333 0.298-0.667 0.298-1.006 0-2.036 4.064-2.708 4.064-3.387s5.424-3.393 6.102-3.393c0.679 0 7.459 1.357 9.488 0.678 2.037-0.678 4.751 0 5.424-0.678 0.679-0.679 3.394-4.745 3.394-6.096 0-1.357 3.162-0.909 4.743-0.679 0.225-0.23 0.679-0.679 0.679-2.714 0-2.03-2.714-5.423-0.679-5.423 2.037 0 7.454-0.679 8.811-1.357 1.357-0.672-0.678-4.065-2.03-4.744-1.356-0.679-2.714 0.679-4.071 1.357-1.351 0.679-4.744 1.357-6.774-0.679-2.035-2.03-0.678-5.423 1.352-8.131 2.035-2.714 4.072-3.394 4.751-6.78 0.672-3.388 1.351-4.066-2.037-4.745-3.387-0.679-2.714 0-4.065 2.708-1.357 2.714-0.678 4.75-2.714 5.422-2.03 0.679-4.744 3.394-5.423 4.072-0.679 0.673 0 3.387 0 6.095 0 2.715 2.715 3.393 2.715 5.423 0 2.036-1.357 1.357-2.715 4.072-1.358 2.708-2.709 7.452-4.745 7.452-2.035 0 0-2.708 0-8.131s-7.452 0.679-8.81 1.357c-1.357 0.672-5.423-0.678-6.102-2.714s0-3.387 0-8.132c0-4.75 4.744-4.071 6.102-5.422 1.357-1.357 4.066-3.393 4.066-7.458 0-4.066 3.387-5.424 4.743-8.811 1.357-3.393 5.424-6.78 6.781-10.167 1.356-3.394 7.452-5.423 9.487-6.78 2.037-1.357 8.812 1.357 8.812 2.708 0 1.357 4.071 2.036 8.137 2.714 4.065 0.679 3.388 2.708 4.065 3.387 0.679 0.679 5.423 3.394 7.459 4.745 2.03 1.357 0.673 4.071 0 5.423l-4.072 1.356-8.81-3.387 2.036 8.132c1.581 1.581 4.744 4.744 5.423 4.744 0.673 0 0.673-2.709 0.673-4.065 1.587 0.902 4.75 2.714 4.75 1.356 0-1.356-0.679-2.035-2.036-4.744-1.357-2.714 2.709-1.812 4.745-1.357 1.132 0.224 3.393 0.679 2.714-1.357-0.394-1.175 0.194-2.575 0.981-3.811v131.92h-0.303c-0.68-1.59-2.04-4.75-2.71-6.11zm137.6-159.29c0.122 0 0.419 0.151 0.831 0.412v1.03c-0.35-0.751-1.14-1.442-0.83-1.442z"
              fill="url(#XMLID_221_)"
            />
            <linearGradient
              id="XMLID_222_"
              y2="185.76"
              gradientUnits="userSpaceOnUse"
              y1="189.85"
              x2="87.13"
              x1="20.782"
            >
              <stop stopColor="#BC9E7A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="25.897 358.96 25.897 20.141 27.266 20.032 27.266 358.82"
              clipRule="evenodd"
              fill="url(#XMLID_222_)"
            />
            <linearGradient
              id="XMLID_223_"
              y2="185.06"
              gradientUnits="userSpaceOnUse"
              y1="189.15"
              x2="87.087"
              x1="20.739"
            >
              <stop stopColor="#BC9E7A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="49.988 356.53 49.988 18.202 51.351 18.086 51.351 356.39"
              clipRule="evenodd"
              fill="url(#XMLID_223_)"
            />
            <linearGradient
              id="XMLID_224_"
              y2="184.31"
              gradientUnits="userSpaceOnUse"
              y1="188.4"
              x2="87.041"
              x1="20.694"
            >
              <stop stopColor="#BC9E7A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="75.885 353.92 75.885 16.111 77.254 16.002 77.254 353.79"
              clipRule="evenodd"
              fill="url(#XMLID_224_)"
            />
            <linearGradient
              id="XMLID_225_"
              y2="27.742"
              gradientUnits="userSpaceOnUse"
              y1="31.832"
              x2="77.389"
              x1="11.041"
            >
              <stop stopColor="#BC9E7A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="16.651 33.173 16.529 31.816 84.07 25.643 84.07 27.012"
              clipRule="evenodd"
              fill="url(#XMLID_225_)"
            />
            <linearGradient
              id="XMLID_226_"
              y2="49.304"
              gradientUnits="userSpaceOnUse"
              y1="53.394"
              x2="78.718"
              x1="12.371"
            >
              <stop stopColor="#BC9E7A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="16.481 54.835 16.36 53.478 84.07 47.279 84.07 48.649"
              clipRule="evenodd"
              fill="url(#XMLID_226_)"
            />
            <linearGradient
              id="XMLID_227_"
              y2="71.108"
              gradientUnits="userSpaceOnUse"
              y1="75.198"
              x2="80.062"
              x1="13.715"
            >
              <stop stopColor="#BC9E7A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.994"
              clipRule="evenodd"
              y="72.264"
              x="16.218"
              fill="url(#XMLID_227_)"
            />
            <linearGradient
              id="XMLID_228_"
              y2="92.063"
              gradientUnits="userSpaceOnUse"
              y1="96.153"
              x2="81.354"
              x1="15.006"
            >
              <stop stopColor="#BC9E7A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.993"
              clipRule="evenodd"
              y="93.298"
              x="16.219"
              fill="url(#XMLID_228_)"
            />
            <linearGradient
              id="XMLID_229_"
              y2="113.62"
              gradientUnits="userSpaceOnUse"
              y1="117.71"
              x2="82.683"
              x1="16.335"
            >
              <stop stopColor="#BC9E7A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="16.36 119.41 16.36 118.04 84.07 111.84 84.07 113.22"
              clipRule="evenodd"
              fill="url(#XMLID_229_)"
            />
            <linearGradient
              id="XMLID_230_"
              y2="135"
              gradientUnits="userSpaceOnUse"
              y1="139.09"
              x2="84.001"
              x1="17.653"
            >
              <stop stopColor="#BC9E7A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="16.36 140.87 16.36 139.49 84.07 133.3 84.07 134.67"
              clipRule="evenodd"
              fill="url(#XMLID_230_)"
            />
            <linearGradient
              id="XMLID_231_"
              y2="156.97"
              gradientUnits="userSpaceOnUse"
              y1="161.06"
              x2="85.355"
              x1="19.008"
            >
              <stop stopColor="#BC9E7A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="16.36 162.92 16.36 161.56 84.07 155.36 84.07 156.73"
              clipRule="evenodd"
              fill="url(#XMLID_231_)"
            />
            <linearGradient
              id="XMLID_232_"
              y2="178.54"
              gradientUnits="userSpaceOnUse"
              y1="182.63"
              x2="86.685"
              x1="20.337"
            >
              <stop stopColor="#BC9E7A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="16.36 184.57 16.36 183.19 84.07 177 84.07 178.37"
              clipRule="evenodd"
              fill="url(#XMLID_232_)"
            />
            <linearGradient
              id="XMLID_233_"
              y2="199.32"
              gradientUnits="userSpaceOnUse"
              y1="203.41"
              x2="87.966"
              x1="21.618"
            >
              <stop stopColor="#BC9E7A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="200.96"
              x="16.218"
              fill="url(#XMLID_233_)"
            />
            <linearGradient
              id="XMLID_234_"
              y2="220.2"
              gradientUnits="userSpaceOnUse"
              y1="224.29"
              x2="89.252"
              x1="22.905"
            >
              <stop stopColor="#BC9E7A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.993"
              clipRule="evenodd"
              y="221.92"
              x="16.219"
              fill="url(#XMLID_234_)"
            />
            <linearGradient
              id="XMLID_235_"
              y2="241.76"
              gradientUnits="userSpaceOnUse"
              y1="245.85"
              x2="90.582"
              x1="24.234"
            >
              <stop stopColor="#BC9E7A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="16.36 248.03 16.36 246.66 84.07 240.46 84.07 241.84"
              clipRule="evenodd"
              fill="url(#XMLID_235_)"
            />
            <linearGradient
              id="XMLID_236_"
              y2="263.56"
              gradientUnits="userSpaceOnUse"
              y1="267.65"
              x2="91.926"
              x1="25.578"
            >
              <stop stopColor="#BC9E7A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="16.36 269.92 16.36 268.55 84.07 262.35 84.07 263.72"
              clipRule="evenodd"
              fill="url(#XMLID_236_)"
            />
            <linearGradient
              id="XMLID_237_"
              y2="284.52"
              gradientUnits="userSpaceOnUse"
              y1="288.6"
              x2="93.217"
              x1="26.87"
            >
              <stop stopColor="#BC9E7A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.993"
              clipRule="evenodd"
              y="286.48"
              x="16.219"
              fill="url(#XMLID_237_)"
            />
            <linearGradient
              id="XMLID_238_"
              y2="306.08"
              gradientUnits="userSpaceOnUse"
              y1="310.17"
              x2="94.547"
              x1="28.199"
            >
              <stop stopColor="#BC9E7A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.993"
              clipRule="evenodd"
              y="308.12"
              x="16.219"
              fill="url(#XMLID_238_)"
            />
            <linearGradient
              id="XMLID_239_"
              y2="328.09"
              gradientUnits="userSpaceOnUse"
              y1="332.18"
              x2="95.903"
              x1="29.556"
            >
              <stop stopColor="#BC9E7A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.993"
              clipRule="evenodd"
              y="330.22"
              x="16.219"
              fill="url(#XMLID_239_)"
            />
            <linearGradient
              id="XMLID_240_"
              y2="349.65"
              gradientUnits="userSpaceOnUse"
              y1="353.74"
              x2="97.233"
              x1="30.885"
            >
              <stop stopColor="#BC9E7A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="16.36 356.33 16.36 354.96 84.07 348.76 84.07 350.14"
              clipRule="evenodd"
              fill="url(#XMLID_240_)"
            />
            <linearGradient
              id="XMLID_241_"
              y2="186.57"
              gradientUnits="userSpaceOnUse"
              y1="190.66"
              x2="222.55"
              x1="156.2"
            >
              <stop stopColor="#77634A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="161.32 359.77 161.32 20.953 162.69 20.843 162.69 359.63"
              clipRule="evenodd"
              fill="url(#XMLID_241_)"
            />
            <linearGradient
              id="XMLID_242_"
              y2="185.88"
              gradientUnits="userSpaceOnUse"
              y1="189.97"
              x2="222.51"
              x1="156.16"
            >
              <stop stopColor="#77634A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="185.41 357.34 185.41 19.014 186.77 18.905 186.77 357.2"
              clipRule="evenodd"
              fill="url(#XMLID_242_)"
            />
            <linearGradient
              id="XMLID_243_"
              y2="185.13"
              gradientUnits="userSpaceOnUse"
              y1="189.22"
              x2="222.46"
              x1="156.11"
            >
              <stop stopColor="#77634A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="211.31 354.74 211.31 16.929 212.68 16.814 212.68 354.6"
              clipRule="evenodd"
              fill="url(#XMLID_243_)"
            />
            <linearGradient
              id="XMLID_244_"
              y2="28.557"
              gradientUnits="userSpaceOnUse"
              y1="32.646"
              x2="212.81"
              x1="146.46"
            >
              <stop stopColor="#77634A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="152.07 33.992 151.95 32.635 219.49 26.455 219.49 27.824"
              clipRule="evenodd"
              fill="url(#XMLID_244_)"
            />
            <linearGradient
              id="XMLID_245_"
              y2="50.118"
              gradientUnits="userSpaceOnUse"
              y1="54.208"
              x2="214.14"
              x1="147.79"
            >
              <stop stopColor="#77634A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="151.9 55.647 151.78 54.29 219.49 48.097 219.49 49.467"
              clipRule="evenodd"
              fill="url(#XMLID_245_)"
            />
            <linearGradient
              id="XMLID_246_"
              y2="71.923"
              gradientUnits="userSpaceOnUse"
              y1="76.013"
              x2="215.48"
              x1="149.14"
            >
              <stop stopColor="#77634A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="73.079"
              x="151.64"
              fill="url(#XMLID_246_)"
            />
            <linearGradient
              id="XMLID_247_"
              y2="92.875"
              gradientUnits="userSpaceOnUse"
              y1="96.965"
              x2="216.78"
              x1="150.43"
            >
              <stop stopColor="#77634A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.994"
              clipRule="evenodd"
              y="94.11"
              x="151.64"
              fill="url(#XMLID_247_)"
            />
            <linearGradient
              id="XMLID_248_"
              y2="114.44"
              gradientUnits="userSpaceOnUse"
              y1="118.53"
              x2="218.1"
              x1="151.76"
            >
              <stop stopColor="#77634A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.994"
              clipRule="evenodd"
              y="115.75"
              x="151.64"
              fill="url(#XMLID_248_)"
            />
            <linearGradient
              id="XMLID_249_"
              y2="135.82"
              gradientUnits="userSpaceOnUse"
              y1="139.91"
              x2="219.42"
              x1="153.07"
            >
              <stop stopColor="#77634A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="137.22"
              x="151.64"
              fill="url(#XMLID_249_)"
            />
            <linearGradient
              id="XMLID_250_"
              y2="157.79"
              gradientUnits="userSpaceOnUse"
              y1="161.88"
              x2="220.78"
              x1="154.43"
            >
              <stop stopColor="#77634A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="159.27"
              x="151.64"
              fill="url(#XMLID_250_)"
            />
            <linearGradient
              id="XMLID_251_"
              y2="179.35"
              gradientUnits="userSpaceOnUse"
              y1="183.44"
              x2="222.11"
              x1="155.76"
            >
              <stop stopColor="#77634A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="151.78 185.38 151.78 184.01 219.49 177.81 219.49 179.19"
              clipRule="evenodd"
              fill="url(#XMLID_251_)"
            />
            <linearGradient
              id="XMLID_252_"
              y2="200.13"
              gradientUnits="userSpaceOnUse"
              y1="204.22"
              x2="223.39"
              x1="157.04"
            >
              <stop stopColor="#77634A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="201.78"
              x="151.64"
              fill="url(#XMLID_252_)"
            />
            <linearGradient
              id="XMLID_253_"
              y2="221.01"
              gradientUnits="userSpaceOnUse"
              y1="225.1"
              x2="224.67"
              x1="158.33"
            >
              <stop stopColor="#77634A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.994"
              clipRule="evenodd"
              y="222.73"
              x="151.64"
              fill="url(#XMLID_253_)"
            />
            <linearGradient
              id="XMLID_254_"
              y2="242.57"
              gradientUnits="userSpaceOnUse"
              y1="246.66"
              x2="226"
              x1="159.66"
            >
              <stop stopColor="#77634A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.994"
              clipRule="evenodd"
              y="244.38"
              x="151.64"
              fill="url(#XMLID_254_)"
            />
            <linearGradient
              id="XMLID_255_"
              y2="264.38"
              gradientUnits="userSpaceOnUse"
              y1="268.47"
              x2="227.35"
              x1="161"
            >
              <stop stopColor="#77634A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.994"
              clipRule="evenodd"
              y="266.26"
              x="151.64"
              fill="url(#XMLID_255_)"
            />
            <linearGradient
              id="XMLID_256_"
              y2="285.33"
              gradientUnits="userSpaceOnUse"
              y1="289.42"
              x2="228.64"
              x1="162.29"
            >
              <stop stopColor="#77634A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="151.78 291.76 151.78 290.39 219.49 284.2 219.49 285.57"
              clipRule="evenodd"
              fill="url(#XMLID_256_)"
            />
            <linearGradient
              id="XMLID_257_"
              y2="306.89"
              gradientUnits="userSpaceOnUse"
              y1="310.98"
              x2="229.97"
              x1="163.62"
            >
              <stop stopColor="#77634A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="308.94"
              x="151.64"
              fill="url(#XMLID_257_)"
            />
            <linearGradient
              id="XMLID_258_"
              y2="328.9"
              gradientUnits="userSpaceOnUse"
              y1="332.99"
              x2="231.33"
              x1="164.98"
            >
              <stop stopColor="#77634A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="331.03"
              x="151.64"
              fill="url(#XMLID_258_)"
            />
            <linearGradient
              id="XMLID_259_"
              y2="350.46"
              gradientUnits="userSpaceOnUse"
              y1="354.55"
              x2="232.65"
              x1="166.31"
            >
              <stop stopColor="#77634A" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.994"
              clipRule="evenodd"
              y="352.68"
              x="151.64"
              fill="url(#XMLID_259_)"
            />
            <linearGradient
              id="XMLID_260_"
              y2="185.66"
              gradientUnits="userSpaceOnUse"
              y1="189.75"
              x2="358.43"
              x1="292.08"
            >
              <stop stopColor="#776347" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="297.19 358.86 297.19 20.043 298.56 19.935 298.56 358.72"
              clipRule="evenodd"
              fill="url(#XMLID_260_)"
            />
            <linearGradient
              id="XMLID_261_"
              y2="184.97"
              gradientUnits="userSpaceOnUse"
              y1="189.06"
              x2="358.38"
              x1="292.04"
            >
              <stop stopColor="#776347" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="321.28 356.43 321.28 18.104 322.65 17.996 322.65 356.29"
              clipRule="evenodd"
              fill="url(#XMLID_261_)"
            />
            <linearGradient
              id="XMLID_262_"
              y2="184.22"
              gradientUnits="userSpaceOnUse"
              y1="188.31"
              x2="358.34"
              x1="291.99"
            >
              <stop stopColor="#776347" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="347.18 353.83 347.18 16.021 348.55 15.905 348.55 353.69"
              clipRule="evenodd"
              fill="url(#XMLID_262_)"
            />
            <linearGradient
              id="XMLID_263_"
              y2="27.648"
              gradientUnits="userSpaceOnUse"
              y1="31.738"
              x2="348.69"
              x1="282.34"
            >
              <stop stopColor="#776347" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="287.95 33.083 287.83 31.726 355.37 25.545 355.37 26.915"
              clipRule="evenodd"
              fill="url(#XMLID_263_)"
            />
            <linearGradient
              id="XMLID_264_"
              y2="49.209"
              gradientUnits="userSpaceOnUse"
              y1="53.299"
              x2="350.02"
              x1="283.67"
            >
              <stop stopColor="#776347" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="287.78 54.738 287.66 53.381 355.37 47.188 355.37 48.558"
              clipRule="evenodd"
              fill="url(#XMLID_264_)"
            />
            <linearGradient
              id="XMLID_265_"
              y2="71.015"
              gradientUnits="userSpaceOnUse"
              y1="75.104"
              x2="351.36"
              x1="285.01"
            >
              <stop stopColor="#776347" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.993"
              clipRule="evenodd"
              y="72.17"
              x="287.52"
              fill="url(#XMLID_265_)"
            />
            <linearGradient
              id="XMLID_266_"
              y2="91.966"
              gradientUnits="userSpaceOnUse"
              y1="96.056"
              x2="352.65"
              x1="286.3"
            >
              <stop stopColor="#776347" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.993"
              clipRule="evenodd"
              y="93.202"
              x="287.52"
              fill="url(#XMLID_266_)"
            />
            <linearGradient
              id="XMLID_267_"
              y2="113.53"
              gradientUnits="userSpaceOnUse"
              y1="117.62"
              x2="353.98"
              x1="287.63"
            >
              <stop stopColor="#776347" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.993"
              clipRule="evenodd"
              y="114.84"
              x="287.52"
              fill="url(#XMLID_267_)"
            />
            <linearGradient
              id="XMLID_268_"
              y2="134.91"
              gradientUnits="userSpaceOnUse"
              y1="139"
              x2="355.3"
              x1="288.95"
            >
              <stop stopColor="#776347" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.993"
              clipRule="evenodd"
              y="136.31"
              x="287.52"
              fill="url(#XMLID_268_)"
            />
            <linearGradient
              id="XMLID_269_"
              y2="156.88"
              gradientUnits="userSpaceOnUse"
              y1="160.97"
              x2="356.65"
              x1="290.3"
            >
              <stop stopColor="#776347" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.992"
              clipRule="evenodd"
              y="158.36"
              x="287.52"
              fill="url(#XMLID_269_)"
            />
            <linearGradient
              id="XMLID_270_"
              y2="178.44"
              gradientUnits="userSpaceOnUse"
              y1="182.53"
              x2="357.98"
              x1="291.63"
            >
              <stop stopColor="#776347" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.993"
              clipRule="evenodd"
              y="180"
              x="287.52"
              fill="url(#XMLID_270_)"
            />
            <linearGradient
              id="XMLID_271_"
              y2="199.22"
              gradientUnits="userSpaceOnUse"
              y1="203.31"
              x2="359.26"
              x1="292.92"
            >
              <stop stopColor="#776347" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.993"
              clipRule="evenodd"
              y="200.87"
              x="287.52"
              fill="url(#XMLID_271_)"
            />
            <linearGradient
              id="XMLID_272_"
              y2="220.1"
              gradientUnits="userSpaceOnUse"
              y1="224.19"
              x2="360.55"
              x1="294.2"
            >
              <stop stopColor="#776347" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.992"
              clipRule="evenodd"
              y="221.82"
              x="287.52"
              fill="url(#XMLID_272_)"
            />
            <linearGradient
              id="XMLID_273_"
              y2="241.66"
              gradientUnits="userSpaceOnUse"
              y1="245.75"
              x2="361.88"
              x1="295.53"
            >
              <stop stopColor="#776347" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.992"
              clipRule="evenodd"
              y="243.47"
              x="287.52"
              fill="url(#XMLID_273_)"
            />
            <linearGradient
              id="XMLID_274_"
              y2="263.47"
              gradientUnits="userSpaceOnUse"
              y1="267.56"
              x2="363.22"
              x1="296.88"
            >
              <stop stopColor="#776347" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.992"
              clipRule="evenodd"
              y="265.35"
              x="287.52"
              fill="url(#XMLID_274_)"
            />
            <linearGradient
              id="XMLID_275_"
              y2="284.42"
              gradientUnits="userSpaceOnUse"
              y1="288.51"
              x2="364.51"
              x1="298.17"
            >
              <stop stopColor="#776347" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="287.66 290.86 287.66 289.48 355.37 283.29 355.37 284.66"
              clipRule="evenodd"
              fill="url(#XMLID_275_)"
            />
            <linearGradient
              id="XMLID_276_"
              y2="305.98"
              gradientUnits="userSpaceOnUse"
              y1="310.07"
              x2="365.84"
              x1="299.5"
            >
              <stop stopColor="#776347" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.993"
              clipRule="evenodd"
              y="308.03"
              x="287.52"
              fill="url(#XMLID_276_)"
            />
            <linearGradient
              id="XMLID_277_"
              y2="327.99"
              gradientUnits="userSpaceOnUse"
              y1="332.08"
              x2="367.2"
              x1="300.85"
            >
              <stop stopColor="#776347" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.993"
              clipRule="evenodd"
              y="330.13"
              x="287.52"
              fill="url(#XMLID_277_)"
            />
            <linearGradient
              id="XMLID_278_"
              y2="349.55"
              gradientUnits="userSpaceOnUse"
              y1="353.64"
              x2="368.53"
              x1="302.18"
            >
              <stop stopColor="#776347" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.993"
              clipRule="evenodd"
              y="351.77"
              x="287.52"
              fill="url(#XMLID_278_)"
            />
            <linearGradient
              id="XMLID_279_"
              y2="184.23"
              gradientUnits="userSpaceOnUse"
              y1="188.73"
              x2="493.95"
              x1="424.88"
            >
              <stop stopColor="#5E4C38" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="432.62 357.64 432.62 18.826 433.98 18.717 433.98 357.51"
              clipRule="evenodd"
              fill="url(#XMLID_279_)"
            />
            <linearGradient
              id="XMLID_280_"
              y2="183.62"
              gradientUnits="userSpaceOnUse"
              y1="188.11"
              x2="493.91"
              x1="424.84"
            >
              <stop stopColor="#5E4C38" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="456.71 355.22 456.71 16.887 458.07 16.778 458.07 355.08"
              clipRule="evenodd"
              fill="url(#XMLID_280_)"
            />
            <linearGradient
              id="XMLID_281_"
              y2="182.96"
              gradientUnits="userSpaceOnUse"
              y1="187.46"
              x2="493.87"
              x1="424.8"
            >
              <stop stopColor="#5E4C38" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="482.6 352.61 482.6 14.802 483.97 14.693 483.97 352.47"
              clipRule="evenodd"
              fill="url(#XMLID_281_)"
            />
            <linearGradient
              id="XMLID_282_"
              y2="26.366"
              gradientUnits="userSpaceOnUse"
              y1="30.859"
              x2="483.68"
              x1="414.61"
            >
              <stop stopColor="#5E4C38" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="423.37 31.865 423.25 30.508 490.79 24.328 490.79 25.697"
              clipRule="evenodd"
              fill="url(#XMLID_282_)"
            />
            <linearGradient
              id="XMLID_283_"
              y2="47.919"
              gradientUnits="userSpaceOnUse"
              y1="52.412"
              x2="485.09"
              x1="416.01"
            >
              <stop stopColor="#5E4C38" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="423.2 53.521 423.08 52.163 490.79 45.971 490.79 47.34"
              clipRule="evenodd"
              fill="url(#XMLID_283_)"
            />
            <linearGradient
              id="XMLID_284_"
              y2="69.714"
              gradientUnits="userSpaceOnUse"
              y1="74.207"
              x2="486.5"
              x1="417.43"
            >
              <stop stopColor="#5E4C38" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.993"
              clipRule="evenodd"
              y="70.952"
              x="422.94"
              fill="url(#XMLID_284_)"
            />
            <linearGradient
              id="XMLID_285_"
              y2="90.658"
              gradientUnits="userSpaceOnUse"
              y1="95.151"
              x2="487.87"
              x1="418.79"
            >
              <stop stopColor="#5E4C38" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="91.987"
              x="422.94"
              fill="url(#XMLID_285_)"
            />
            <linearGradient
              id="XMLID_286_"
              y2="112.21"
              gradientUnits="userSpaceOnUse"
              y1="116.7"
              x2="489.27"
              x1="420.19"
            >
              <stop stopColor="#5E4C38" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="113.63"
              x="422.94"
              fill="url(#XMLID_286_)"
            />
            <linearGradient
              id="XMLID_287_"
              y2="133.58"
              gradientUnits="userSpaceOnUse"
              y1="138.07"
              x2="490.66"
              x1="421.58"
            >
              <stop stopColor="#5E4C38" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.994"
              clipRule="evenodd"
              y="135.09"
              x="422.94"
              fill="url(#XMLID_287_)"
            />
            <linearGradient
              id="XMLID_288_"
              y2="155.54"
              gradientUnits="userSpaceOnUse"
              y1="160.04"
              x2="492.05"
              x1="422.98"
            >
              <stop stopColor="#5E4C38" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="157.14"
              x="422.94"
              fill="url(#XMLID_288_)"
            />
            <linearGradient
              id="XMLID_289_"
              y2="177.09"
              gradientUnits="userSpaceOnUse"
              y1="181.59"
              x2="493.49"
              x1="424.42"
            >
              <stop stopColor="#5E4C38" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="178.79"
              x="422.94"
              fill="url(#XMLID_289_)"
            />
            <linearGradient
              id="XMLID_290_"
              y2="197.87"
              gradientUnits="userSpaceOnUse"
              y1="202.36"
              x2="494.84"
              x1="425.77"
            >
              <stop stopColor="#5E4C38" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.993"
              clipRule="evenodd"
              y="199.65"
              x="422.94"
              fill="url(#XMLID_290_)"
            />
            <linearGradient
              id="XMLID_291_"
              y2="218.74"
              gradientUnits="userSpaceOnUse"
              y1="223.23"
              x2="496.2"
              x1="427.12"
            >
              <stop stopColor="#5E4C38" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.994"
              clipRule="evenodd"
              y="220.61"
              x="422.94"
              fill="url(#XMLID_291_)"
            />
            <linearGradient
              id="XMLID_292_"
              y2="240.29"
              gradientUnits="userSpaceOnUse"
              y1="244.78"
              x2="497.6"
              x1="428.53"
            >
              <stop stopColor="#5E4C38" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="242.25"
              x="422.94"
              fill="url(#XMLID_292_)"
            />
            <linearGradient
              id="XMLID_293_"
              y2="262.08"
              gradientUnits="userSpaceOnUse"
              y1="266.58"
              x2="499.02"
              x1="429.94"
            >
              <stop stopColor="#5E4C38" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="264.14"
              x="422.94"
              fill="url(#XMLID_293_)"
            />
            <linearGradient
              id="XMLID_294_"
              y2="283.03"
              gradientUnits="userSpaceOnUse"
              y1="287.52"
              x2="500.38"
              x1="431.3"
            >
              <stop stopColor="#5E4C38" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="285.17"
              x="422.94"
              fill="url(#XMLID_294_)"
            />
            <linearGradient
              id="XMLID_295_"
              y2="304.58"
              gradientUnits="userSpaceOnUse"
              y1="309.07"
              x2="501.78"
              x1="432.71"
            >
              <stop stopColor="#5E4C38" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.994"
              clipRule="evenodd"
              y="306.81"
              x="422.94"
              fill="url(#XMLID_295_)"
            />
            <linearGradient
              id="XMLID_296_"
              y2="326.59"
              gradientUnits="userSpaceOnUse"
              y1="331.08"
              x2="503.21"
              x1="434.14"
            >
              <stop stopColor="#5E4C38" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="423.08 333.38 423.08 332 490.79 325.81 490.79 327.18"
              clipRule="evenodd"
              fill="url(#XMLID_296_)"
            />
            <linearGradient
              id="XMLID_297_"
              y2="348.13"
              gradientUnits="userSpaceOnUse"
              y1="352.63"
              x2="504.61"
              x1="435.54"
            >
              <stop stopColor="#5E4C38" offset="0" />
              <stop stopColor="#B79C78" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="350.55"
              x="422.94"
              fill="url(#XMLID_297_)"
            />
            <linearGradient
              id="XMLID_298_"
              y2="185.81"
              gradientUnits="userSpaceOnUse"
              y1="189.9"
              x2="80.561"
              x1="146.9"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="141.78 359 141.78 20.189 140.42 20.08 140.42 358.87"
              clipRule="evenodd"
              fill="url(#XMLID_298_)"
            />
            <linearGradient
              id="XMLID_299_"
              y2="185.12"
              gradientUnits="userSpaceOnUse"
              y1="189.21"
              x2="80.599"
              x1="146.95"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="117.7 356.58 117.7 18.25 116.33 18.141 116.33 356.45"
              clipRule="evenodd"
              fill="url(#XMLID_299_)"
            />
            <linearGradient
              id="XMLID_300_"
              y2="184.37"
              gradientUnits="userSpaceOnUse"
              y1="188.46"
              x2="80.647"
              x1="146.99"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="91.796 353.97 91.796 16.166 90.433 16.057 90.433 353.83"
              clipRule="evenodd"
              fill="url(#XMLID_300_)"
            />
            <linearGradient
              id="XMLID_301_"
              y2="27.794"
              gradientUnits="userSpaceOnUse"
              y1="31.883"
              x2="90.297"
              x1="156.64"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="151.03 33.228 151.15 31.871 83.616 25.691 83.616 27.06"
              clipRule="evenodd"
              fill="url(#XMLID_301_)"
            />
            <linearGradient
              id="XMLID_302_"
              y2="49.355"
              gradientUnits="userSpaceOnUse"
              y1="53.445"
              x2="88.967"
              x1="155.32"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="151.2 54.883 151.33 53.526 83.616 47.334 83.616 48.703"
              clipRule="evenodd"
              fill="url(#XMLID_302_)"
            />
            <linearGradient
              id="XMLID_303_"
              y2="71.161"
              gradientUnits="userSpaceOnUse"
              y1="75.25"
              x2="87.624"
              x1="153.97"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.994"
              clipRule="evenodd"
              y="72.315"
              x="83.474"
              fill="url(#XMLID_303_)"
            />
            <linearGradient
              id="XMLID_304_"
              y2="92.115"
              gradientUnits="userSpaceOnUse"
              y1="96.205"
              x2="86.331"
              x1="152.68"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="93.35"
              x="83.474"
              fill="url(#XMLID_304_)"
            />
            <linearGradient
              id="XMLID_305_"
              y2="113.67"
              gradientUnits="userSpaceOnUse"
              y1="117.76"
              x2="85.003"
              x1="151.35"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="114.99"
              x="83.474"
              fill="url(#XMLID_305_)"
            />
            <linearGradient
              id="XMLID_306_"
              y2="135.05"
              gradientUnits="userSpaceOnUse"
              y1="139.14"
              x2="83.686"
              x1="150.03"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.994"
              clipRule="evenodd"
              y="136.45"
              x="83.474"
              fill="url(#XMLID_306_)"
            />
            <linearGradient
              id="XMLID_307_"
              y2="157.03"
              gradientUnits="userSpaceOnUse"
              y1="161.12"
              x2="82.33"
              x1="148.68"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.993"
              clipRule="evenodd"
              y="158.51"
              x="83.474"
              fill="url(#XMLID_307_)"
            />
            <linearGradient
              id="XMLID_308_"
              y2="178.59"
              gradientUnits="userSpaceOnUse"
              y1="182.68"
              x2="81.003"
              x1="147.35"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="180.15"
              x="83.474"
              fill="url(#XMLID_308_)"
            />
            <linearGradient
              id="XMLID_309_"
              y2="199.37"
              gradientUnits="userSpaceOnUse"
              y1="203.46"
              x2="79.721"
              x1="146.07"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="201.01"
              x="83.474"
              fill="url(#XMLID_309_)"
            />
            <linearGradient
              id="XMLID_310_"
              y2="220.25"
              gradientUnits="userSpaceOnUse"
              y1="224.34"
              x2="78.434"
              x1="144.78"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="221.97"
              x="83.474"
              fill="url(#XMLID_310_)"
            />
            <linearGradient
              id="XMLID_311_"
              y2="241.81"
              gradientUnits="userSpaceOnUse"
              y1="245.9"
              x2="77.106"
              x1="143.45"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.993"
              clipRule="evenodd"
              y="243.61"
              x="83.474"
              fill="url(#XMLID_311_)"
            />
            <linearGradient
              id="XMLID_312_"
              y2="263.61"
              gradientUnits="userSpaceOnUse"
              y1="267.7"
              x2="75.761"
              x1="142.11"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.994"
              clipRule="evenodd"
              y="265.5"
              x="83.474"
              fill="url(#XMLID_312_)"
            />
            <linearGradient
              id="XMLID_313_"
              y2="284.57"
              gradientUnits="userSpaceOnUse"
              y1="288.66"
              x2="74.47"
              x1="140.82"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="286.53"
              x="83.474"
              fill="url(#XMLID_313_)"
            />
            <linearGradient
              id="XMLID_314_"
              y2="306.13"
              gradientUnits="userSpaceOnUse"
              y1="310.22"
              x2="73.14"
              x1="139.49"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="308.17"
              x="83.474"
              fill="url(#XMLID_314_)"
            />
            <linearGradient
              id="XMLID_315_"
              y2="328.14"
              gradientUnits="userSpaceOnUse"
              y1="332.23"
              x2="71.784"
              x1="138.13"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="151.33 334.74 151.33 333.37 83.616 327.18 83.616 328.54"
              clipRule="evenodd"
              fill="url(#XMLID_315_)"
            />
            <linearGradient
              id="XMLID_316_"
              y2="349.7"
              gradientUnits="userSpaceOnUse"
              y1="353.79"
              x2="70.456"
              x1="136.8"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="351.91"
              x="83.474"
              fill="url(#XMLID_316_)"
            />
            <linearGradient
              id="XMLID_317_"
              y2="186.26"
              gradientUnits="userSpaceOnUse"
              y1="190.35"
              x2="216.89"
              x1="283.23"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="278.11 359.46 278.11 20.644 276.75 20.535 276.75 359.32"
              clipRule="evenodd"
              fill="url(#XMLID_317_)"
            />
            <linearGradient
              id="XMLID_318_"
              y2="185.57"
              gradientUnits="userSpaceOnUse"
              y1="189.66"
              x2="216.93"
              x1="283.28"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="254.03 357.03 254.03 18.705 252.66 18.595 252.66 356.9"
              clipRule="evenodd"
              fill="url(#XMLID_318_)"
            />
            <linearGradient
              id="XMLID_319_"
              y2="184.83"
              gradientUnits="userSpaceOnUse"
              y1="188.91"
              x2="216.98"
              x1="283.33"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="228.13 354.43 228.13 16.62 226.76 16.511 226.76 354.29"
              clipRule="evenodd"
              fill="url(#XMLID_319_)"
            />
            <linearGradient
              id="XMLID_320_"
              y2="28.248"
              gradientUnits="userSpaceOnUse"
              y1="32.337"
              x2="226.63"
              x1="292.97"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="287.36 33.683 287.48 32.326 219.95 26.146 219.95 27.515"
              clipRule="evenodd"
              fill="url(#XMLID_320_)"
            />
            <linearGradient
              id="XMLID_321_"
              y2="49.811"
              gradientUnits="userSpaceOnUse"
              y1="53.9"
              x2="225.3"
              x1="291.65"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="287.53 55.338 287.66 53.98 219.95 47.789 219.95 49.158"
              clipRule="evenodd"
              fill="url(#XMLID_321_)"
            />
            <linearGradient
              id="XMLID_322_"
              y2="71.616"
              gradientUnits="userSpaceOnUse"
              y1="75.705"
              x2="223.95"
              x1="290.3"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.993"
              clipRule="evenodd"
              y="72.77"
              x="219.8"
              fill="url(#XMLID_322_)"
            />
            <linearGradient
              id="XMLID_323_"
              y2="92.57"
              gradientUnits="userSpaceOnUse"
              y1="96.659"
              x2="222.66"
              x1="289.01"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="93.804"
              x="219.8"
              fill="url(#XMLID_323_)"
            />
            <linearGradient
              id="XMLID_324_"
              y2="114.13"
              gradientUnits="userSpaceOnUse"
              y1="118.22"
              x2="221.33"
              x1="287.68"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="115.44"
              x="219.8"
              fill="url(#XMLID_324_)"
            />
            <linearGradient
              id="XMLID_325_"
              y2="135.51"
              gradientUnits="userSpaceOnUse"
              y1="139.6"
              x2="220.02"
              x1="286.36"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="136.91"
              x="219.8"
              fill="url(#XMLID_325_)"
            />
            <linearGradient
              id="XMLID_326_"
              y2="157.48"
              gradientUnits="userSpaceOnUse"
              y1="161.57"
              x2="218.66"
              x1="285.01"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="158.96"
              x="219.8"
              fill="url(#XMLID_326_)"
            />
            <linearGradient
              id="XMLID_327_"
              y2="179.04"
              gradientUnits="userSpaceOnUse"
              y1="183.13"
              x2="217.33"
              x1="283.68"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="180.6"
              x="219.8"
              fill="url(#XMLID_327_)"
            />
            <linearGradient
              id="XMLID_328_"
              y2="199.82"
              gradientUnits="userSpaceOnUse"
              y1="203.91"
              x2="216.05"
              x1="282.4"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="201.47"
              x="219.8"
              fill="url(#XMLID_328_)"
            />
            <linearGradient
              id="XMLID_329_"
              y2="220.71"
              gradientUnits="userSpaceOnUse"
              y1="224.8"
              x2="214.77"
              x1="281.11"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.994"
              clipRule="evenodd"
              y="222.43"
              x="219.8"
              fill="url(#XMLID_329_)"
            />
            <linearGradient
              id="XMLID_330_"
              y2="242.26"
              gradientUnits="userSpaceOnUse"
              y1="246.35"
              x2="213.44"
              x1="279.78"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="244.07"
              x="219.8"
              fill="url(#XMLID_330_)"
            />
            <linearGradient
              id="XMLID_331_"
              y2="264.07"
              gradientUnits="userSpaceOnUse"
              y1="268.16"
              x2="212.09"
              x1="278.44"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="265.95"
              x="219.8"
              fill="url(#XMLID_331_)"
            />
            <linearGradient
              id="XMLID_332_"
              y2="285.02"
              gradientUnits="userSpaceOnUse"
              y1="289.11"
              x2="210.8"
              x1="277.15"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="286.99"
              x="219.8"
              fill="url(#XMLID_332_)"
            />
            <linearGradient
              id="XMLID_333_"
              y2="306.58"
              gradientUnits="userSpaceOnUse"
              y1="310.67"
              x2="209.47"
              x1="275.82"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="308.63"
              x="219.8"
              fill="url(#XMLID_333_)"
            />
            <linearGradient
              id="XMLID_334_"
              y2="328.6"
              gradientUnits="userSpaceOnUse"
              y1="332.69"
              x2="208.11"
              x1="274.46"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="287.66 335.2 287.66 333.82 219.95 327.63 219.95 329"
              clipRule="evenodd"
              fill="url(#XMLID_334_)"
            />
            <linearGradient
              id="XMLID_335_"
              y2="350.16"
              gradientUnits="userSpaceOnUse"
              y1="354.24"
              x2="206.79"
              x1="273.13"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="352.37"
              x="219.8"
              fill="url(#XMLID_335_)"
            />
            <linearGradient
              id="XMLID_336_"
              y2="185.66"
              gradientUnits="userSpaceOnUse"
              y1="189.75"
              x2="351.4"
              x1="417.75"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="412.63 358.86 412.63 20.043 411.26 19.935 411.26 358.72"
              clipRule="evenodd"
              fill="url(#XMLID_336_)"
            />
            <linearGradient
              id="XMLID_337_"
              y2="184.97"
              gradientUnits="userSpaceOnUse"
              y1="189.06"
              x2="351.44"
              x1="417.79"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="388.54 356.43 388.54 18.104 387.17 17.996 387.17 356.29"
              clipRule="evenodd"
              fill="url(#XMLID_337_)"
            />
            <linearGradient
              id="XMLID_338_"
              y2="184.22"
              gradientUnits="userSpaceOnUse"
              y1="188.31"
              x2="351.49"
              x1="417.84"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="362.64 353.83 362.64 16.021 361.28 15.905 361.28 353.69"
              clipRule="evenodd"
              fill="url(#XMLID_338_)"
            />
            <linearGradient
              id="XMLID_339_"
              y2="27.648"
              gradientUnits="userSpaceOnUse"
              y1="31.738"
              x2="361.14"
              x1="427.49"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="421.87 33.083 421.99 31.726 354.46 25.545 354.46 26.915"
              clipRule="evenodd"
              fill="url(#XMLID_339_)"
            />
            <linearGradient
              id="XMLID_340_"
              y2="49.21"
              gradientUnits="userSpaceOnUse"
              y1="53.3"
              x2="359.81"
              x1="426.16"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="422.04 54.738 422.17 53.381 354.46 47.188 354.46 48.558"
              clipRule="evenodd"
              fill="url(#XMLID_340_)"
            />
            <linearGradient
              id="XMLID_341_"
              y2="71.016"
              gradientUnits="userSpaceOnUse"
              y1="75.105"
              x2="358.47"
              x1="424.82"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.994"
              clipRule="evenodd"
              y="72.17"
              x="354.32"
              fill="url(#XMLID_341_)"
            />
            <linearGradient
              id="XMLID_342_"
              y2="91.967"
              gradientUnits="userSpaceOnUse"
              y1="96.057"
              x2="357.18"
              x1="423.52"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="93.202"
              x="354.32"
              fill="url(#XMLID_342_)"
            />
            <linearGradient
              id="XMLID_343_"
              y2="113.53"
              gradientUnits="userSpaceOnUse"
              y1="117.62"
              x2="355.85"
              x1="422.19"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.994"
              clipRule="evenodd"
              y="114.84"
              x="354.32"
              fill="url(#XMLID_343_)"
            />
            <linearGradient
              id="XMLID_344_"
              y2="134.91"
              gradientUnits="userSpaceOnUse"
              y1="139"
              x2="354.53"
              x1="420.88"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.994"
              clipRule="evenodd"
              y="136.31"
              x="354.32"
              fill="url(#XMLID_344_)"
            />
            <linearGradient
              id="XMLID_345_"
              y2="156.88"
              gradientUnits="userSpaceOnUse"
              y1="160.97"
              x2="353.17"
              x1="419.52"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.993"
              clipRule="evenodd"
              y="158.36"
              x="354.32"
              fill="url(#XMLID_345_)"
            />
            <linearGradient
              id="XMLID_346_"
              y2="178.44"
              gradientUnits="userSpaceOnUse"
              y1="182.53"
              x2="351.84"
              x1="418.19"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.994"
              clipRule="evenodd"
              y="180"
              x="354.32"
              fill="url(#XMLID_346_)"
            />
            <linearGradient
              id="XMLID_347_"
              y2="199.22"
              gradientUnits="userSpaceOnUse"
              y1="203.31"
              x2="350.57"
              x1="416.91"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="200.87"
              x="354.32"
              fill="url(#XMLID_347_)"
            />
            <linearGradient
              id="XMLID_348_"
              y2="220.1"
              gradientUnits="userSpaceOnUse"
              y1="224.19"
              x2="349.28"
              x1="415.62"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.993"
              clipRule="evenodd"
              y="221.82"
              x="354.32"
              fill="url(#XMLID_348_)"
            />
            <linearGradient
              id="XMLID_349_"
              y2="241.66"
              gradientUnits="userSpaceOnUse"
              y1="245.75"
              x2="347.95"
              x1="414.3"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.993"
              clipRule="evenodd"
              y="243.47"
              x="354.32"
              fill="url(#XMLID_349_)"
            />
            <linearGradient
              id="XMLID_350_"
              y2="263.47"
              gradientUnits="userSpaceOnUse"
              y1="267.56"
              x2="346.6"
              x1="412.95"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.993"
              clipRule="evenodd"
              y="265.35"
              x="354.32"
              fill="url(#XMLID_350_)"
            />
            <linearGradient
              id="XMLID_351_"
              y2="284.42"
              gradientUnits="userSpaceOnUse"
              y1="288.51"
              x2="345.31"
              x1="411.66"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="422.17 290.86 422.17 289.48 354.46 283.29 354.46 284.66"
              clipRule="evenodd"
              fill="url(#XMLID_351_)"
            />
            <linearGradient
              id="XMLID_352_"
              y2="305.98"
              gradientUnits="userSpaceOnUse"
              y1="310.07"
              x2="343.98"
              x1="410.33"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="308.03"
              x="354.32"
              fill="url(#XMLID_352_)"
            />
            <linearGradient
              id="XMLID_353_"
              y2="328"
              gradientUnits="userSpaceOnUse"
              y1="332.08"
              x2="342.63"
              x1="408.98"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="330.13"
              x="354.32"
              fill="url(#XMLID_353_)"
            />
            <linearGradient
              id="XMLID_354_"
              y2="349.56"
              gradientUnits="userSpaceOnUse"
              y1="353.64"
              x2="341.3"
              x1="407.65"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="351.77"
              x="354.32"
              fill="url(#XMLID_354_)"
            />
            <linearGradient
              id="XMLID_355_"
              y2="184.9"
              gradientUnits="userSpaceOnUse"
              y1="188.99"
              x2="486.82"
              x1="553.17"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="548.05 358.09 548.05 19.28 546.68 19.171 546.68 357.96"
              clipRule="evenodd"
              fill="url(#XMLID_355_)"
            />
            <linearGradient
              id="XMLID_356_"
              y2="184.21"
              gradientUnits="userSpaceOnUse"
              y1="188.3"
              x2="486.87"
              x1="553.21"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="523.96 355.67 523.96 17.341 522.59 17.232 522.59 355.54"
              clipRule="evenodd"
              fill="url(#XMLID_356_)"
            />
            <linearGradient
              id="XMLID_357_"
              y2="183.46"
              gradientUnits="userSpaceOnUse"
              y1="187.55"
              x2="486.91"
              x1="553.26"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="498.06 353.06 498.06 15.256 496.7 15.148 496.7 352.93"
              clipRule="evenodd"
              fill="url(#XMLID_357_)"
            />
            <linearGradient
              id="XMLID_358_"
              y2="26.885"
              gradientUnits="userSpaceOnUse"
              y1="30.975"
              x2="496.56"
              x1="562.91"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="557.29 32.319 557.42 30.962 489.88 24.782 489.88 26.151"
              clipRule="evenodd"
              fill="url(#XMLID_358_)"
            />
            <linearGradient
              id="XMLID_359_"
              y2="48.447"
              gradientUnits="userSpaceOnUse"
              y1="52.537"
              x2="495.23"
              x1="561.58"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="557.46 53.975 557.59 52.617 489.88 46.425 489.88 47.794"
              clipRule="evenodd"
              fill="url(#XMLID_359_)"
            />
            <linearGradient
              id="XMLID_360_"
              y2="70.252"
              gradientUnits="userSpaceOnUse"
              y1="74.341"
              x2="493.89"
              x1="560.24"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.994"
              clipRule="evenodd"
              y="71.407"
              x="489.74"
              fill="url(#XMLID_360_)"
            />
            <linearGradient
              id="XMLID_361_"
              y2="91.207"
              gradientUnits="userSpaceOnUse"
              y1="95.296"
              x2="492.6"
              x1="558.94"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.994"
              clipRule="evenodd"
              y="92.441"
              x="489.74"
              fill="url(#XMLID_361_)"
            />
            <linearGradient
              id="XMLID_362_"
              y2="112.76"
              gradientUnits="userSpaceOnUse"
              y1="116.85"
              x2="491.27"
              x1="557.62"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="114.08"
              x="489.74"
              fill="url(#XMLID_362_)"
            />
            <linearGradient
              id="XMLID_363_"
              y2="134.14"
              gradientUnits="userSpaceOnUse"
              y1="138.23"
              x2="489.95"
              x1="556.3"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="135.54"
              x="489.74"
              fill="url(#XMLID_363_)"
            />
            <linearGradient
              id="XMLID_364_"
              y2="156.12"
              gradientUnits="userSpaceOnUse"
              y1="160.21"
              x2="488.59"
              x1="554.94"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.993"
              clipRule="evenodd"
              y="157.6"
              x="489.74"
              fill="url(#XMLID_364_)"
            />
            <linearGradient
              id="XMLID_365_"
              y2="177.68"
              gradientUnits="userSpaceOnUse"
              y1="181.77"
              x2="487.27"
              x1="553.61"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.993"
              clipRule="evenodd"
              y="179.24"
              x="489.74"
              fill="url(#XMLID_365_)"
            />
            <linearGradient
              id="XMLID_366_"
              y2="198.46"
              gradientUnits="userSpaceOnUse"
              y1="202.55"
              x2="485.99"
              x1="552.33"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.993"
              clipRule="evenodd"
              y="200.1"
              x="489.74"
              fill="url(#XMLID_366_)"
            />
            <linearGradient
              id="XMLID_367_"
              y2="219.34"
              gradientUnits="userSpaceOnUse"
              y1="223.43"
              x2="484.7"
              x1="551.05"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="221.06"
              x="489.74"
              fill="url(#XMLID_367_)"
            />
            <linearGradient
              id="XMLID_368_"
              y2="240.9"
              gradientUnits="userSpaceOnUse"
              y1="244.99"
              x2="483.37"
              x1="549.72"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="242.7"
              x="489.74"
              fill="url(#XMLID_368_)"
            />
            <linearGradient
              id="XMLID_369_"
              y2="262.7"
              gradientUnits="userSpaceOnUse"
              y1="266.79"
              x2="482.03"
              x1="548.37"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="264.59"
              x="489.74"
              fill="url(#XMLID_369_)"
            />
            <linearGradient
              id="XMLID_370_"
              y2="283.66"
              gradientUnits="userSpaceOnUse"
              y1="287.75"
              x2="480.73"
              x1="547.08"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="285.62"
              x="489.74"
              fill="url(#XMLID_370_)"
            />
            <linearGradient
              id="XMLID_371_"
              y2="305.22"
              gradientUnits="userSpaceOnUse"
              y1="309.31"
              x2="479.4"
              x1="545.75"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.37"
              width="67.994"
              clipRule="evenodd"
              y="307.26"
              x="489.74"
              fill="url(#XMLID_371_)"
            />
            <linearGradient
              id="XMLID_372_"
              y2="327.23"
              gradientUnits="userSpaceOnUse"
              y1="331.32"
              x2="478.05"
              x1="544.4"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="557.59 333.83 557.59 332.46 489.88 326.27 489.88 327.64"
              clipRule="evenodd"
              fill="url(#XMLID_372_)"
            />
            <linearGradient
              id="XMLID_373_"
              y2="348.79"
              gradientUnits="userSpaceOnUse"
              y1="352.88"
              x2="476.72"
              x1="543.07"
            >
              <stop stopColor="#786147" offset="0" />
              <stop stopColor="#8A7052" offset="1" />
            </linearGradient>
            <rect
              fillRule="evenodd"
              height="1.369"
              width="67.994"
              clipRule="evenodd"
              y="351"
              x="489.74"
              fill="url(#XMLID_373_)"
            />
            <path
              clipRule="evenodd"
              d="m449.39 237.3l-9.471-2.582c1.722-6.313-0.411-10.694-4.622-13.875-4.593-3.467-11.779-5.508-19.39-5.617-7.616-0.103-14.688 1.745-19.062 4.975-3.927 2.896-5.89 6.931-3.945 13.106l-9.366 2.945c-3.273-10.398 0.362-18.693 7.482-23.946 6.658-4.92 16.087-7.016 25.023-6.896 8.956 0.128 18.408 2.491 25.17 7.598 7.13 5.38 11.06 13.75 8.19 24.29z"
              fillRule="evenodd"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m384.11 236.06c-3.163-10.24 0.375-18.353 7.302-23.515 6.524-4.866 15.808-6.98 24.666-6.854 8.877 0.122 18.184 2.503 24.8 7.563 6.387 4.89 10.101 12.239 8.435 21.498-0.261 0.739-0.563 1.502-0.915 2.278-2.484-1.272-5.096-2.491-7.805-3.654 1.304-5.962-0.854-10.186-4.9-13.282-4.715-3.617-12.04-5.708-19.729-5.817-7.695-0.109-14.918 1.782-19.443 5.168-4.139 3.103-6.223 7.49-4.212 14.051l-8.21 2.56z"
              fill="#292929"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m384.7 235.88c-3.065-10.088 0.376-18.013 7.107-23.085 6.399-4.816 15.536-6.943 24.322-6.816 8.792 0.127 17.953 2.515 24.424 7.532 6.308 4.896 9.906 12.246 8.065 21.559-0.364 0.557-0.765 1.121-1.207 1.702-1.812-1.684-3.872-3.253-6.138-4.719 0.892-5.618-1.296-9.683-5.18-12.688-4.841-3.769-12.301-5.914-20.067-6.023-7.781-0.109-15.148 1.818-19.826 5.362-4.351 3.302-6.556 8.047-4.472 14.99l-7.02 2.19z"
              fill="#555554"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m385.28 235.68c-2.956-9.924 0.388-17.662 6.926-22.643 6.259-4.775 15.263-6.907 23.964-6.78 8.707 0.121 17.729 2.52 24.061 7.495 6.223 4.902 9.707 12.251 7.695 21.625-0.472 0.363-0.975 0.732-1.503 1.114-1.139-2.09-2.653-4.018-4.471-5.787 0.472-5.271-1.739-9.167-5.46-12.088-4.969-3.926-12.561-6.113-20.413-6.228-7.853-0.116-15.371 1.86-20.201 5.561-4.557 3.497-6.889 8.599-4.732 15.93l-5.87 1.81z"
              fill="#808080"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m385.86 235.49c-2.849-9.767 0.4-17.323 6.737-22.213 6.133-4.726 14.99-6.871 23.613-6.744 8.628 0.128 17.504 2.539 23.69 7.471 6.145 4.902 9.507 12.246 7.326 21.686-0.582 0.17-1.176 0.352-1.794 0.533-0.467-2.502-1.436-4.786-2.812-6.853 0.066-4.927-2.175-8.665-5.732-11.495-5.096-4.084-12.82-6.319-20.752-6.434-7.938-0.115-15.603 1.896-20.582 5.756-4.77 3.696-7.224 9.155-5 16.868l-4.71 1.41z"
              fill="#A8A8A8"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m386.45 235.3c-2.745-9.61 0.4-16.978 6.55-21.771 5.992-4.678 14.711-6.84 23.255-6.707 8.543 0.121 17.28 2.544 23.322 7.435 6.064 4.907 9.313 12.251 6.955 21.752-0.685-0.019-1.382-0.037-2.084-0.049 0.212-2.914-0.213-5.55-1.146-7.919-0.352-4.581-2.617-8.162-6.011-10.9-5.218-4.236-13.081-6.52-21.092-6.641-8.016-0.122-15.826 1.933-20.958 5.949-4.981 3.896-7.563 9.713-5.266 17.809l-3.53 1.04z"
              fill="#D5D4D4"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m445.84 236.32l-2.368-0.63c2.272-8.598-0.503-14.966-5.774-19.292-5.345-4.394-13.343-6.726-21.432-6.847-8.095-0.122-16.057 1.969-21.34 6.144-5.192 4.102-7.896 10.27-5.526 18.753l-2.363 0.661c-2.636-9.453 0.412-16.632 6.368-21.34 5.859-4.629 14.433-6.799 22.897-6.671 8.465 0.127 17.057 2.557 22.958 7.404 5.96 4.93 9.09 12.27 6.57 21.83z"
              fill="#fff"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m385.52 230.42c-0.285-4.478 0.697-8.531 2.757-11.924 1-1.654 2.266-3.223 3.806-4.568 0.928-0.818 1.908-1.521 2.932-2.157 1.813-1.115 3.793-1.926 5.878-2.478 1.024-0.267 2.085-0.498 3.182-0.625 1.157-0.133 2.345-0.224 3.581-0.23 0.526-0.006 1.055 0.036 1.581 0.036-0.545 0.091-1.097 0.158-1.63 0.248-0.976 0.176-1.934 0.364-2.86 0.619-0.902 0.249-1.799 0.503-2.665 0.824-1.558 0.575-3.042 1.248-4.467 2.083-1.006 0.588-1.962 1.273-2.884 2.03-1.423 1.175-2.672 2.617-3.689 4.078-0.679 0.981-1.23 2.048-1.69 3.139-0.879 2.108-1.382 4.399-1.437 6.919-0.049 2.181 0.249 4.229 0.787 6.131l-2.375 0.618c-0.4-1.51-0.7-3.08-0.8-4.74z"
              fill="#6E6E6E"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m445.63 235.79l-2.363-0.648c0.539-1.921 0.866-3.909 0.896-6.09 0.03-1.945-0.291-3.738-0.738-5.441-0.158-0.593-0.371-1.169-0.601-1.727-0.975-2.382-2.418-4.538-4.381-6.41-0.357-0.339-0.721-0.667-1.097-0.982-0.915-0.775-1.896-1.46-2.902-2.066-2.49-1.509-5.199-2.611-8.04-3.423-1.601-0.454-3.229-0.848-4.902-1.139 5.192 0.048 9.858 1.048 14.008 3.163 1.212 0.618 2.353 1.369 3.461 2.205 1.685 1.272 3.156 2.933 4.332 4.624 0.775 1.115 1.394 2.351 1.891 3.605 0.939 2.363 1.43 4.944 1.412 7.786-0.02 2.36-0.42 4.49-0.98 6.56z"
              fill="#6E6E6E"
            />
            <linearGradient
              id="XMLID_374_"
              y2="457.23"
              gradientUnits="userSpaceOnUse"
              y1="249.18"
              x2="478.75"
              x1="350.71"
            >
              <stop stopColor="#7C7C7D" offset="0" />
              <stop offset="1" />
            </linearGradient>
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m414.75 229.33c68.401 0 123.92 55.514 123.92 123.92s-55.514 123.92-123.92 123.92c-68.401 0.006-123.92-55.507-123.92-123.91 0-68.42 55.52-123.93 123.92-123.93z"
              fill="url(#XMLID_374_)"
            />
            <linearGradient
              id="XMLID_375_"
              y2="452.45"
              gradientUnits="userSpaceOnUse"
              y1="260"
              x2="485.41"
              x1="348.35"
            >
              <stop stopColor="#BBBBBA" offset="0" />
              <stop stopColor="#fff" offset=".13" />
              <stop stopColor="#fff" offset=".25" />
              <stop stopColor="#3B3B3B" offset=".4" />
              <stop stopColor="#fff" offset=".51" />
              <stop stopColor="#1F1F1F" offset=".62" />
              <stop stopColor="#fff" offset=".73" />
              <stop stopColor="#242424" offset=".84" />
              <stop stopColor="#fff" offset="1" />
            </linearGradient>
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m414.75 235.43c65.032 0 117.81 52.781 117.81 117.81 0 65.033-52.781 117.81-117.81 117.81s-117.81-52.78-117.81-117.81c0-65.03 52.78-117.81 117.81-117.81z"
              fill="url(#XMLID_375_)"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m414.75 245.75c59.331 0 107.49 48.158 107.49 107.49s-48.16 107.49-107.49 107.49-107.49-48.152-107.49-107.48c0-59.34 48.16-107.49 107.49-107.5z"
              fill="#4F4F4F"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m310.13 353.25c0 57.75 46.874 104.62 104.62 104.62 57.75 0 104.62-46.873 104.62-104.62 0-57.75-46.867-104.62-104.62-104.62-57.75-0.01-104.62 46.86-104.62 104.62z"
              fill="#636263"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m313 353.24c0 56.168 45.583 101.75 101.75 101.75s101.75-45.583 101.75-101.75-45.587-101.75-101.75-101.75c-56.17 0-101.75 45.59-101.75 101.75z"
              fill="#757575"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m315.87 353.24c0 54.587 44.304 98.885 98.885 98.885 54.587 0 98.885-44.298 98.885-98.885 0-54.58-44.305-98.885-98.885-98.879-54.59 0-98.89 44.3-98.89 98.88z"
              fill="#878787"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m318.73 353.24c0.006 53.006 43.02 96.02 96.019 96.02 52.999-0.007 96.013-43.021 96.013-96.02 0-53-43.014-96.013-96.013-96.013s-96.02 43.01-96.02 96.01z"
              fill="#9C9C9C"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m321.61 353.24c0 51.418 41.729 93.146 93.146 93.146 51.418 0 93.147-41.729 93.147-93.146s-41.729-93.147-93.147-93.147c-51.42 0-93.15 41.73-93.15 93.15z"
              fill="#ADADAD"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m324.47 353.24c0 49.837 40.445 90.281 90.281 90.281s90.281-40.444 90.281-90.281c0-49.83-40.45-90.275-90.281-90.275-49.83 0.01-90.28 40.45-90.28 90.28z"
              fill="#BFBFBF"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m327.34 353.24c0 48.249 39.16 87.409 87.409 87.409s87.408-39.16 87.408-87.409-39.159-87.409-87.408-87.409-87.41 39.16-87.41 87.41z"
              fill="#D2D1D2"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m330.21 353.24c0 46.667 37.875 84.543 84.542 84.543 46.668 0 84.543-37.876 84.543-84.543s-37.875-84.543-84.543-84.543c-46.66 0-84.54 37.87-84.54 84.54z"
              fill="#E6E6E6"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m414.75 271.57c45.086 0 81.678 36.585 81.678 81.671 0 45.087-36.592 81.678-81.678 81.678s-81.671-36.591-81.671-81.678c0-45.08 36.59-81.67 81.67-81.67z"
              fill="#F7F7F7"
            />
            <linearGradient
              id="XMLID_376_"
              y2="242.71"
              gradientUnits="userSpaceOnUse"
              y1="376.95"
              x2="487.04"
              x1="285.67"
            >
              <stop stopColor="#3B3B3B" offset="0" />
              <stop stopColor="#fff" offset=".12" />
              <stop stopColor="#FCFCFC" offset=".46" />
              <stop stopColor="#fff" offset=".83" />
              <stop stopColor="#555554" offset="1" />
            </linearGradient>
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m292.63 354.08c0-68.086 55.259-123.34 123.34-123.34 41.238 0 77.77 20.268 100.17 51.38-22.605-25.483-55.592-41.552-92.304-41.552-68.087 0-123.34 55.253-123.34 123.33 0 26.849 8.592 51.703 23.176 71.958-19.32-21.77-31.05-50.42-31.05-81.78z"
              fill="url(#XMLID_376_)"
            />
            <path
              d="m466.43 265.04c-0.297-0.108-0.612-0.194-0.897-0.32-0.225-0.098-0.441-0.231-0.654-0.346-0.539-0.285-1.072-0.582-1.605-0.879-0.631-0.352-1.266-0.696-1.903-1.048-2.792-1.521-5.616-2.969-8.507-4.284-7.949-3.605-16.383-6.296-25.424-7.726-3.811-0.6-7.713-0.981-11.754-1.12-4.188-0.146-8.416 0.018-12.421 0.406-8.296 0.799-16.082 2.635-23.479 5.247-2.981 1.055-5.901 2.248-8.75 3.569-5.974 2.775-11.682 6.083-17.093 9.985-3.557 2.563-6.985 5.369-10.288 8.453-2.647 2.479-5.193 5.132-7.525 7.822-3.659 4.223-6.89 8.664-9.726 13.3-4.404 7.174-7.834 14.845-10.288 22.988-1.254 4.144-2.235 8.423-3.011 12.802-0.146 0.818-0.279 1.637-0.419 2.455-0.121 0.715-0.241 1.43-0.376 2.139-0.078 0.418-0.217 0.77-0.351 1.151-0.066-0.339-0.152-0.667-0.176-1.024-0.079-0.933-0.024-1.933 0.013-2.86 0.091-2.271 0.32-4.471 0.624-6.64 0.933-6.689 2.659-12.991 4.943-18.996 2.813-7.398 6.568-14.348 11.149-20.854 2.848-4.049 6.035-7.932 9.585-11.628 3.388-3.514 6.969-6.763 10.677-9.682 7.464-5.86 15.518-10.616 24.121-14.197 5.478-2.279 11.179-4.132 17.136-5.447 5.429-1.194 11.069-1.981 16.978-2.224 1.024-0.042 2.054-0.066 3.096-0.079 4.975-0.037 9.768 0.309 14.391 0.981 6.853 1.006 13.348 2.739 19.517 5.102 5.55 2.127 10.846 4.763 15.862 7.956 1.278 0.812 2.533 1.661 3.757 2.564 0.643 0.466 1.272 0.957 1.885 1.472 0.34 0.3 0.61 0.63 0.91 0.97z"
              fill="#575757"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m525.06 357.45c0 58.053-47.116 105.16-105.17 105.17-35.162 0-66.306-17.286-85.404-43.814 19.269 21.729 47.395 35.434 78.702 35.434 58.053-0.006 105.16-47.115 105.16-105.17 0-22.892-7.326-44.08-19.759-61.349 16.47 18.56 26.46 42.98 26.47 69.73z"
              fill="#DFDFDF"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m410.74 464.5c-13.664-1.313-26.091-5.271-37.307-11.469-2.382-1.315-4.701-2.738-6.962-4.296-0.793-0.546-1.575-1.115-2.345-1.702-0.673-0.517-1.327-1.042-1.97-1.612-0.369-0.327-0.623-0.696-0.908-1.085 0.321 0.098 0.667 0.164 0.981 0.285 0.242 0.091 0.473 0.229 0.703 0.346 0.575 0.284 1.145 0.587 1.721 0.89 0.672 0.358 1.351 0.722 2.023 1.072 2.969 1.57 5.968 3.061 9.034 4.43 11.415 5.091 23.745 8.398 37.378 9.064 6.375 0.315 12.828-0.012 18.814-0.848 5.302-0.733 10.409-1.86 15.348-3.32 3.581-1.055 7.054-2.297 10.459-3.696 4.931-2.029 9.688-4.418 14.269-7.15 8.397-5.017 16.257-11.185 23.382-18.655 1.594-1.667 3.115-3.375 4.575-5.114 2.26-2.702 4.368-5.489 6.319-8.355 6.841-10.034 11.827-20.978 14.947-32.87 0.667-2.533 1.243-5.108 1.764-7.714 0.206-1.023 0.4-2.061 0.594-3.09 0.109-0.576 0.219-1.152 0.333-1.728 0.043-0.248 0.067-0.448 0.134-0.696 0.03-0.115 0.079-0.23 0.109-0.346 0.139-0.467 0.303-0.927 0.454-1.387 0.061 0.357 0.121 0.721 0.188 1.077 0.043 0.261 0.098 0.467 0.121 0.728 0.025 0.236 0.007 0.473 0.007 0.715 0.029 2.915-0.194 5.732-0.552 8.477-0.751 5.799-2.06 11.313-3.812 16.621-2.847 8.634-6.871 16.698-11.925 24.236-4.435 6.623-9.791 12.924-15.916 18.607-2.019 1.872-4.078 3.647-6.192 5.339-7.943 6.343-16.518 11.396-25.68 15.305-8.513 3.636-17.619 6.217-27.302 7.501-2.8 0.37-5.653 0.636-8.567 0.775-4.73 0.22-9.62 0.1-14.2-0.34z"
              fill="#0F0F0E"
            />
            <path
              clipRule="evenodd"
              d="m414.05 450.17v-9.622c0.236 0.007 0.468 0.007 0.703 0.007 0.237 0 0.473-0.007 0.709-0.007v9.622h-1.41zm0.7-184.23h-0.703v-9.628h1.406l0.006 9.628h-0.72z"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="m424.19 449.71l-1.011-9.567c0.472-0.043 0.938-0.091 1.405-0.146l1.006 9.566-1.4 0.15zm-19.26-183.22l-1.006-9.568 1.405-0.145 1.006 9.567c-0.47 0.03-0.94 0.08-1.4 0.14z"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="m434.24 448.21l-1.999-9.416c0.454-0.091 0.914-0.188 1.375-0.291l1.999 9.416-1.38 0.29zm-38.31-180.23l-1.994-9.379 1.376-0.297 1.993 9.386c-0.46 0.09-0.92 0.18-1.38 0.29z"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="m444.07 445.67l-2.976-9.167c0.449-0.14 0.892-0.285 1.34-0.437l2.981 9.168-1.34 0.44zm-56.93-175.27l-2.957-9.101 1.339-0.436 2.957 9.101c-0.45 0.15-0.9 0.3-1.34 0.44z"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="m453.59 442.07l-3.921-8.791c0.438-0.188 0.861-0.382 1.291-0.575l3.915 8.798-1.29 0.57zm-74.94-168.33l-3.902-8.761 1.29-0.576 3.896 8.761c-0.44 0.2-0.87 0.38-1.29 0.58z"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="m462.66 437.54l-4.822-8.355c0.406-0.23 0.812-0.467 1.218-0.703l4.823 8.355-1.22 0.7zm-92.13-159.58l-4.799-8.313 1.217-0.703 4.8 8.307c-0.4 0.23-0.82 0.46-1.22 0.7z"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="m471.2 432.09l-5.684-7.823c0.381-0.271 0.763-0.544 1.139-0.829l5.684 7.822-1.14 0.83zm-108.3-149.08l-5.642-7.763 1.14-0.83 5.641 7.768c-0.38 0.27-0.76 0.54-1.14 0.82z"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="m479.14 425.75l-6.471-7.187c0.352-0.314 0.697-0.63 1.042-0.944l6.478 7.186-1.05 0.94zm-123.29-136.94l-6.422-7.138 1.048-0.938 6.424 7.131c-0.35 0.32-0.7 0.63-1.05 0.95z"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="m486.35 418.62l-7.18-6.471c0.314-0.346 0.63-0.697 0.939-1.049l7.186 6.471-0.95 1.05zm-136.91-123.29l-7.138-6.429 0.945-1.048 7.138 6.429c-0.31 0.34-0.63 0.7-0.94 1.05z"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="m492.81 410.78l-7.828-5.684c0.278-0.383 0.558-0.758 0.83-1.141l7.822 5.685-0.82 1.14zm-149.06-108.33l-7.762-5.635 0.83-1.14 7.762 5.641c-0.28 0.38-0.56 0.75-0.83 1.13z"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="m498.39 402.29l-8.356-4.827c0.237-0.4 0.474-0.807 0.703-1.219l8.356 4.823-0.7 1.23zm-159.57-92.15l-8.306-4.799 0.702-1.218 8.313 4.799c-0.23 0.4-0.47 0.81-0.7 1.22z"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="m503.05 393.29l-8.804-3.921c0.193-0.423 0.388-0.854 0.575-1.284l8.804 3.921-0.57 1.28zm-168.33-74.97l-8.756-3.901 0.569-1.285 8.762 3.902c-0.19 0.42-0.38 0.85-0.57 1.28z"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="m506.75 383.85l-9.155-2.976c0.145-0.449 0.291-0.891 0.43-1.339l9.162 2.975-0.44 1.35zm-175.25-56.97l-9.12-2.963 0.438-1.339 9.118 2.963c-0.16 0.45-0.3 0.9-0.44 1.34z"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="m509.44 374.07l-9.422-1.999c0.097-0.461 0.194-0.921 0.291-1.382l9.421 2.006-0.29 1.37zm-180.24-38.33l-9.372-1.993 0.291-1.375 9.379 1.993c-0.11 0.45-0.2 0.91-0.3 1.37z"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="m511.09 364.06l-9.579-1.006c0.049-0.466 0.097-0.933 0.146-1.399l9.585 1.006-0.15 1.4zm-183.24-19.28l-9.549-1.006 0.151-1.398 9.549 1.006c-0.06 0.46-0.1 0.94-0.15 1.4z"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="m414.05 450.17l-0.005-9.628c0.236 0 0.473 0.007 0.708 0.007 0.23 0 0.467-0.007 0.703-0.007v9.622l-1.4 0.01zm0.7-184.23h-0.708v-9.621h1.411v9.621h-0.7z"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="m405.32 449.71l-1.399-0.146 1.006-9.566c0.466 0.055 0.933 0.103 1.398 0.146l-1.01 9.57zm17.86-183.37l1.006-9.567 1.399 0.145-1.006 9.568c-0.47-0.06-0.94-0.11-1.4-0.15z"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="m395.32 448.19l-1.381-0.29 1.992-9.38c0.461 0.098 0.922 0.193 1.382 0.291l-1.99 9.38zm36.91-180.5l2-9.416 1.381 0.291-2.005 9.416c-0.45-0.09-0.91-0.2-1.37-0.29z"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="m385.52 445.63l-1.345-0.437 2.963-9.106c0.442 0.152 0.891 0.291 1.339 0.436l-2.96 9.11zm55.57-175.64l2.98-9.167 1.339 0.437-2.975 9.168c-0.45-0.15-0.9-0.3-1.35-0.44z"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="m376.03 442.09l-1.284-0.57 3.902-8.767c0.424 0.193 0.854 0.387 1.284 0.575l-3.9 8.77zm73.63-168.88l3.915-8.792 1.291 0.569-3.921 8.798c-0.42-0.2-0.85-0.39-1.29-0.58z"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="m366.95 437.54l-1.217-0.703 4.799-8.313c0.399 0.242 0.812 0.479 1.218 0.709l-4.8 8.3zm90.88-160.24l4.823-8.354 1.218 0.709-4.816 8.35c-0.4-0.24-0.81-0.48-1.22-0.71z"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="m358.4 432.07l-1.14-0.83 5.642-7.762c0.375 0.279 0.757 0.552 1.139 0.831l-5.64 7.76zm107.12-149.85l5.678-7.816 1.139 0.824-5.678 7.822c-0.39-0.27-0.76-0.54-1.14-0.82z"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="m350.48 425.76l-1.048-0.945 6.422-7.138c0.346 0.315 0.697 0.631 1.05 0.946l-6.42 7.13zm122.18-137.83l6.471-7.191 1.049 0.945-6.472 7.186c-0.35-0.32-0.7-0.63-1.05-0.94z"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="m343.24 418.64l-0.945-1.042 7.138-6.43c0.315 0.352 0.631 0.697 0.945 1.043l-7.14 6.43zm135.93-124.31l7.186-6.466 0.939 1.043-7.18 6.471c-0.32-0.34-0.63-0.69-0.95-1.04z"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="m336.81 410.82l-0.824-1.14 7.762-5.641c0.272 0.382 0.552 0.764 0.83 1.139l-7.77 5.64zm148.17-109.43l7.822-5.684 0.83 1.14-7.822 5.688c-0.27-0.39-0.55-0.76-0.83-1.15z"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="m331.22 402.37l-0.702-1.225 8.306-4.798c0.231 0.411 0.468 0.818 0.71 1.224l-8.32 4.81zm158.82-93.34l8.351-4.829 0.709 1.225-8.356 4.823c-0.23-0.4-0.47-0.81-0.7-1.21z"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="m326.53 393.36l-0.576-1.29 8.763-3.902c0.188 0.431 0.381 0.86 0.575 1.29l-8.75 3.9zm167.72-76.24l8.804-3.921 0.569 1.285-8.798 3.921c-0.18-0.42-0.38-0.85-0.57-1.28z"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="m322.82 383.91l-0.438-1.338 9.12-2.964c0.139 0.448 0.284 0.897 0.436 1.339l-9.12 2.96zm174.77-58.29l9.155-2.975 0.437 1.339-9.162 2.98c-0.14-0.45-0.28-0.89-0.43-1.34z"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="m320.12 374.12l-0.297-1.382 9.378-1.993c0.098 0.461 0.195 0.921 0.298 1.376l-9.38 1.99zm179.9-39.7l9.422-2.006 0.296 1.382-9.421 1.999c-0.1-0.45-0.2-0.91-0.3-1.37z"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="m318.44 364.12l-0.145-1.405 9.549-1.006c0.049 0.473 0.098 0.938 0.151 1.405l-9.56 1zm183.07-20.68l9.579-1.006 0.151 1.399-9.585 1.006c-0.05-0.47-0.1-0.94-0.15-1.4z"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="m511.68 353.94h-9.629c0.006-0.23 0.006-0.467 0.006-0.697 0-0.242 0-0.479-0.006-0.715l9.629 0.007v1.41zm-184.23-0.01h-9.628v-1.411h9.628v1.407z"
              fillRule="evenodd"
            />
            <linearGradient
              id="XMLID_377_"
              y2="443.58"
              gradientUnits="userSpaceOnUse"
              y1="260.52"
              x2="475.91"
              x1="353.87"
            >
              <stop stopColor="#C2C2C2" offset="0" />
              <stop stopColor="#F9FAFA" offset=".5" />
              <stop stopColor="#1F1F1F" offset="1" />
            </linearGradient>
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m301.63 351.84c0-62.439 50.679-113.12 113.12-113.12 62.439 0 113.12 50.679 113.12 113.12 0 62.445-50.679 113.12-113.12 113.12-62.44 0-113.12-50.68-113.12-113.12zm218.27 0c0-58.034-47.109-105.14-105.14-105.14-58.04 0-105.14 47.104-105.14 105.14 0 58.04 47.104 105.14 105.14 105.14 58.03 0 105.14-47.1 105.14-105.14z"
              fill="url(#XMLID_377_)"
            />
            <polygon points="402.37 293.13 402.38 269.01 407.11 269.01 416.98 285.12 416.98 269.01 421.5 269.01 421.5 293.13 416.62 293.13 406.9 277.4 406.9 293.13" />
            <polygon points="331.44 360.61 325.88 337.29 330.7 337.29 334.21 353.31 338.47 337.29 344.07 337.29 348.16 353.57 351.73 337.29 356.48 337.29 350.81 360.61 345.82 360.61 341.17 343.18 336.55 360.61" />
            <path d="m403.1 431.39l5.484-0.533c0.333 1.842 0.999 3.192 2.011 4.053 1.006 0.866 2.369 1.297 4.085 1.297 1.817 0 3.187-0.382 4.102-1.151 0.921-0.77 1.381-1.666 1.381-2.69 0-0.66-0.188-1.224-0.581-1.69-0.388-0.467-1.061-0.867-2.023-1.205-0.66-0.23-2.169-0.637-4.515-1.225-3.023-0.745-5.145-1.666-6.361-2.756-1.716-1.54-2.569-3.413-2.569-5.618 0-1.424 0.399-2.75 1.205-3.992 0.807-1.236 1.97-2.182 3.49-2.824 1.515-0.647 3.345-0.976 5.49-0.976 3.508 0 6.144 0.77 7.912 2.31 1.771 1.531 2.703 3.58 2.788 6.144l-5.635 0.249c-0.237-1.431-0.758-2.46-1.552-3.092-0.793-0.629-1.987-0.944-3.568-0.944-1.642 0-2.921 0.339-3.848 1.006-0.6 0.436-0.896 1.018-0.896 1.739 0 0.66 0.278 1.224 0.842 1.69 0.703 0.599 2.436 1.224 5.175 1.872 2.744 0.648 4.773 1.314 6.089 2.005 1.31 0.691 2.339 1.637 3.085 2.837 0.739 1.199 1.108 2.684 1.108 4.446 0 1.6-0.442 3.098-1.327 4.496-0.891 1.394-2.145 2.437-3.774 3.114-1.624 0.68-3.648 1.019-6.071 1.019-3.533 0-6.241-0.818-8.138-2.448-1.89-1.66-3.02-4.04-3.38-7.16z" />
            <polygon points="481.45 361.09 481.45 336.68 499.56 336.68 499.56 340.81 486.38 340.81 486.38 346.22 498.64 346.22 498.64 350.34 486.38 350.34 486.38 356.98 500.02 356.98 500.02 361.09" />
            <linearGradient
              id="XMLID_378_"
              y2="332.65"
              gradientUnits="userSpaceOnUse"
              y1="338.06"
              x2="408.66"
              x1="403.25"
            >
              <stop stopColor="#212121" offset="0" />
              <stop stopColor="#DBDBDA" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="390.82 325.64 415.67 350.48 421.72 344.43"
              clipRule="evenodd"
              fill="url(#XMLID_378_)"
            />
            <linearGradient
              id="XMLID_379_"
              y2="338.06"
              gradientUnits="userSpaceOnUse"
              y1="343.48"
              x2="403.25"
              x1="397.83"
            >
              <stop stopColor="#BBBBBA" offset="0" />
              <stop stopColor="#fff" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="390.82 325.64 415.67 350.48 409.62 356.54"
              clipRule="evenodd"
              fill="url(#XMLID_379_)"
            />
            <linearGradient
              id="XMLID_380_"
              y2="357.49"
              gradientUnits="userSpaceOnUse"
              y1="362.9"
              x2="433.51"
              x1="428.09"
            >
              <stop stopColor="#212121" offset="0" />
              <stop stopColor="#DBDBDA" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="440.52 375.33 415.67 350.48 421.72 344.43"
              clipRule="evenodd"
              fill="url(#XMLID_380_)"
            />
            <linearGradient
              id="XMLID_381_"
              y2="362.91"
              gradientUnits="userSpaceOnUse"
              y1="368.32"
              x2="428.09"
              x1="422.68"
            >
              <stop stopColor="#BBBBBA" offset="0" />
              <stop stopColor="#fff" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="440.52 375.33 415.67 350.48 409.62 356.54"
              clipRule="evenodd"
              fill="url(#XMLID_381_)"
            />
            <linearGradient
              id="XMLID_382_"
              y2="368.32"
              gradientUnits="userSpaceOnUse"
              y1="362.9"
              x2="408.66"
              x1="403.25"
            >
              <stop stopColor="#212121" offset="0" />
              <stop stopColor="#DBDBDA" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="390.82 375.33 415.67 350.48 415.67 360.22"
              clipRule="evenodd"
              fill="url(#XMLID_382_)"
            />
            <linearGradient
              id="XMLID_383_"
              y2="362.9"
              gradientUnits="userSpaceOnUse"
              y1="357.49"
              x2="403.25"
              x1="397.83"
            >
              <stop stopColor="#BBBBBA" offset="0" />
              <stop stopColor="#fff" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="390.82 375.33 415.67 350.48 405.94 350.48"
              clipRule="evenodd"
              fill="url(#XMLID_383_)"
            />
            <linearGradient
              id="XMLID_384_"
              y2="332.65"
              gradientUnits="userSpaceOnUse"
              y1="338.06"
              x2="422.68"
              x1="428.1"
            >
              <stop stopColor="#212121" offset="0" />
              <stop stopColor="#DBDBDA" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="440.52 325.64 415.67 350.48 415.67 340.75"
              clipRule="evenodd"
              fill="url(#XMLID_384_)"
            />
            <linearGradient
              id="XMLID_385_"
              y2="338.06"
              gradientUnits="userSpaceOnUse"
              y1="343.48"
              x2="428.09"
              x1="433.51"
            >
              <stop stopColor="#BBBBBA" offset="0" />
              <stop stopColor="#fff" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="440.52 325.64 415.67 350.48 425.4 350.48"
              clipRule="evenodd"
              fill="url(#XMLID_385_)"
            />
            <linearGradient
              id="XMLID_386_"
              y2="323.64"
              gradientUnits="userSpaceOnUse"
              y1="323.64"
              x2="427.37"
              x1="415.67"
            >
              <stop stopColor="#212121" offset="0" />
              <stop stopColor="#DBDBDA" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="415.67 296.81 415.67 350.48 428.75 350.48"
              clipRule="evenodd"
              fill="url(#XMLID_386_)"
            />
            <linearGradient
              id="XMLID_387_"
              y2="323.64"
              gradientUnits="userSpaceOnUse"
              y1="323.64"
              x2="415.67"
              x1="403.97"
            >
              <stop stopColor="#BBBBBA" offset="0" />
              <stop stopColor="#fff" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="415.67 296.81 415.67 350.48 402.59 350.48"
              clipRule="evenodd"
              fill="url(#XMLID_387_)"
            />
            <linearGradient
              id="XMLID_388_"
              y2="377.32"
              gradientUnits="userSpaceOnUse"
              y1="377.32"
              x2="427.37"
              x1="415.67"
            >
              <stop stopColor="#212121" offset="0" />
              <stop stopColor="#DBDBDA" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="415.67 404.16 415.67 350.48 428.75 350.48"
              clipRule="evenodd"
              fill="url(#XMLID_388_)"
            />
            <linearGradient
              id="XMLID_389_"
              y2="377.32"
              gradientUnits="userSpaceOnUse"
              y1="377.32"
              x2="415.67"
              x1="403.97"
            >
              <stop stopColor="#BBBBBA" offset="0" />
              <stop stopColor="#fff" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="415.67 404.16 415.67 350.48 402.6 350.48"
              clipRule="evenodd"
              fill="url(#XMLID_389_)"
            />
            <linearGradient
              id="XMLID_390_"
              y2="362.18"
              gradientUnits="userSpaceOnUse"
              y1="350.48"
              x2="388.84"
              x1="388.84"
            >
              <stop stopColor="#212121" offset="0" />
              <stop stopColor="#DBDBDA" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="362 350.48 415.67 350.48 405.16 361"
              clipRule="evenodd"
              fill="url(#XMLID_390_)"
            />
            <linearGradient
              id="XMLID_391_"
              y2="350.48"
              gradientUnits="userSpaceOnUse"
              y1="338.78"
              x2="388.84"
              x1="388.84"
            >
              <stop stopColor="#BBBBBA" offset="0" />
              <stop stopColor="#fff" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="362 350.48 415.67 350.48 405.16 339.96"
              clipRule="evenodd"
              fill="url(#XMLID_391_)"
            />
            <linearGradient
              id="XMLID_392_"
              y2="338.78"
              gradientUnits="userSpaceOnUse"
              y1="350.48"
              x2="442.51"
              x1="442.51"
            >
              <stop stopColor="#212121" offset="0" />
              <stop stopColor="#DBDBDA" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="469.35 350.48 415.67 350.48 426.2 339.97"
              clipRule="evenodd"
              fill="url(#XMLID_392_)"
            />
            <linearGradient
              id="XMLID_393_"
              y2="350.48"
              gradientUnits="userSpaceOnUse"
              y1="362.18"
              x2="442.51"
              x1="442.51"
            >
              <stop stopColor="#BBBBBA" offset="0" />
              <stop stopColor="#fff" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="469.35 350.48 415.67 350.48 426.19 361"
              clipRule="evenodd"
              fill="url(#XMLID_393_)"
            />
            <linearGradient
              id="XMLID_394_"
              y2="319.21"
              gradientUnits="userSpaceOnUse"
              y1="319.21"
              x2="427.48"
              x1="415.78"
            >
              <stop stopColor="#25252E" offset="0" />
              <stop stopColor="#7D8AA1" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="415.78 287.01 415.78 351.42 428.85 351.42"
              clipRule="evenodd"
              fill="url(#XMLID_394_)"
            />
            <linearGradient
              id="XMLID_395_"
              y2="319.21"
              gradientUnits="userSpaceOnUse"
              y1="319.21"
              x2="415.78"
              x1="404.08"
            >
              <stop stopColor="#21252E" offset="0" />
              <stop stopColor="#C9D9EA" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="415.77 287 415.77 351.42 402.7 351.42"
              clipRule="evenodd"
              fill="url(#XMLID_395_)"
            />
            <linearGradient
              id="XMLID_396_"
              y2="383.62"
              gradientUnits="userSpaceOnUse"
              y1="383.62"
              x2="427.48"
              x1="415.78"
            >
              <stop stopColor="#570A07" offset="0" />
              <stop stopColor="#E59494" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="415.78 415.83 415.78 351.42 428.85 351.42"
              clipRule="evenodd"
              fill="url(#XMLID_396_)"
            />
            <linearGradient
              id="XMLID_397_"
              y2="383.62"
              gradientUnits="userSpaceOnUse"
              y1="383.62"
              x2="415.78"
              x1="404.08"
            >
              <stop stopColor="#520A06" offset="0" />
              <stop stopColor="#F3ABAC" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="415.78 415.83 415.78 351.42 402.7 351.42"
              clipRule="evenodd"
              fill="url(#XMLID_397_)"
            />
            <linearGradient
              id="XMLID_398_"
              y2="362.5"
              gradientUnits="userSpaceOnUse"
              y1="337.26"
              x2="423.43"
              x1="407.9"
            >
              <stop stopColor="#7C7C7D" offset="0" />
              <stop offset="1" />
            </linearGradient>
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m415.66 334.85c8.295 0 15.033 6.736 15.033 15.032s-6.738 15.032-15.033 15.032-15.027-6.736-15.027-15.032 6.74-15.03 15.03-15.03z"
              fill="url(#XMLID_398_)"
            />
            <linearGradient
              id="XMLID_399_"
              y2="361.91"
              gradientUnits="userSpaceOnUse"
              y1="338.57"
              x2="424.23"
              x1="407.61"
            >
              <stop stopColor="#BBBBBA" offset="0" />
              <stop stopColor="#fff" offset=".13" />
              <stop stopColor="#fff" offset=".25" />
              <stop stopColor="#3B3B3B" offset=".4" />
              <stop stopColor="#fff" offset=".51" />
              <stop stopColor="#1F1F1F" offset=".62" />
              <stop stopColor="#fff" offset=".73" />
              <stop stopColor="#242424" offset=".84" />
              <stop stopColor="#fff" offset="1" />
            </linearGradient>
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m415.66 335.6c7.89 0 14.293 6.397 14.293 14.287 0 7.889-6.403 14.287-14.293 14.287-7.889 0-14.288-6.398-14.288-14.287 0-7.89 6.4-14.29 14.29-14.29z"
              fill="url(#XMLID_399_)"
            />
            <path
              clipRule="evenodd"
              d="m415.66 336.84c7.198 0 13.04 5.841 13.04 13.039s-5.842 13.039-13.04 13.039-13.039-5.841-13.039-13.039 5.84-13.04 13.04-13.04z"
              fillRule="evenodd"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m403.29 349.33c0 6.635 5.466 12.021 12.204 12.021 6.737 0 12.202-5.387 12.202-12.021s-5.465-12.021-12.202-12.021c-6.73-0.01-12.2 5.38-12.2 12.02z"
              fill="#191919"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m403.96 348.77c0 6.079 5.09 11.004 11.367 11.004 6.271 0 11.367-4.925 11.367-11.004 0-6.07-5.096-11.002-11.367-11.002-6.29 0-11.37 4.93-11.37 11z"
              fill="#363636"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m404.62 348.21c0 5.514 4.721 9.986 10.536 9.986 5.812 0 10.531-4.473 10.531-9.986 0-5.508-4.72-9.985-10.531-9.985-5.82 0.01-10.54 4.48-10.54 9.99z"
              fill="#525252"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m405.28 347.65c0 4.951 4.345 8.968 9.701 8.968 5.35 0 9.694-4.017 9.694-8.968 0-4.95-4.345-8.967-9.694-8.967-5.35 0.01-9.7 4.02-9.7 8.97z"
              fill="#6E6E6E"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m405.95 347.1c0 4.393 3.969 7.95 8.857 7.95 4.896 0 8.865-3.558 8.865-7.95 0-4.388-3.97-7.95-8.865-7.95-4.9 0-8.87 3.56-8.87 7.95z"
              fill="#89898A"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m406.62 346.54c0 3.83 3.593 6.932 8.023 6.932 4.435 0 8.028-3.102 8.028-6.932 0-3.823-3.594-6.932-8.028-6.932-4.43 0-8.02 3.11-8.02 6.93z"
              fill="#A6A6A6"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m407.28 345.98c0 3.267 3.225 5.914 7.193 5.914s7.192-2.647 7.192-5.914c0-3.266-3.224-5.913-7.192-5.913s-7.19 2.65-7.19 5.91z"
              fill="#BFBFBF"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m407.94 345.42c0 2.702 2.848 4.901 6.356 4.901s6.356-2.199 6.356-4.901-2.849-4.896-6.356-4.896c-3.51 0.01-6.36 2.2-6.36 4.9z"
              fill="#DBDBDA"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m414.13 340.99c3.048 0 5.521 1.74 5.521 3.878 0 2.146-2.473 3.884-5.521 3.884-3.047 0-5.52-1.738-5.52-3.884 0-2.14 2.47-3.88 5.52-3.88z"
              fill="#F7F7F7"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m429.04 350.39c0 7.041-5.713 12.761-12.754 12.761-4.266 0-8.041-2.096-10.361-5.314 2.339 2.63 5.75 4.296 9.55 4.296 7.041 0 12.754-5.714 12.754-12.754 0-2.781-0.891-5.351-2.399-7.44 2 2.24 3.21 5.2 3.21 8.44z"
              fill="#DFDFDF"
            />
            <linearGradient
              id="XMLID_400_"
              y2="360.84"
              gradientUnits="userSpaceOnUse"
              y1="338.64"
              x2="423.08"
              x1="408.28"
            >
              <stop stopColor="#C2C2C2" offset="0" />
              <stop stopColor="#F9FAFA" offset=".5" />
              <stop stopColor="#1F1F1F" offset="1" />
            </linearGradient>
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="m401.94 349.71c0-7.574 6.144-13.718 13.718-13.718s13.724 6.144 13.724 13.718-6.149 13.725-13.724 13.725-13.72-6.15-13.72-13.73zm26.48 0c0-7.041-5.713-12.755-12.754-12.755s-12.755 5.714-12.755 12.755 5.714 12.755 12.755 12.755 12.74-5.72 12.74-12.77z"
              fill="url(#XMLID_400_)"
            />
            <linearGradient
              id="XMLID_401_"
              y2="232.34"
              gradientUnits="userSpaceOnUse"
              y1="239.7"
              x2="388.06"
              x1="389.29"
            >
              <stop stopColor="#fff" offset="0" />
              <stop stopColor="#5C5C5C" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="382.85 233.62 392.67 231.16 394.51 238.52 384.69 240.98"
              clipRule="evenodd"
              fill="url(#XMLID_401_)"
            />
            <linearGradient
              id="XMLID_402_"
              y2="233.27"
              gradientUnits="userSpaceOnUse"
              y1="240.63"
              x2="445.93"
              x1="442.86"
            >
              <stop stopColor="#F1F0F0" offset="0" />
              <stop stopColor="#5C5C5C" offset="1" />
            </linearGradient>
            <polygon
              fillRule="evenodd"
              points="450.33 234.23 440.52 231.78 438.68 239.14 448.49 241.6"
              clipRule="evenodd"
              fill="url(#XMLID_402_)"
            />
          </g>
        </g>
      </switch>
    </svg>
  );

  useEffect(() => {
    tale.image =
      "https://localhost:3000/static/media/fl.17491d4e3f90eeb4a36e.jpeg";

    console.debug(tale);
  }, []);

  function onOpenItinerary(tale) {
    const slug = tale?.id ?? null;
    // ??
    // encodeURIComponent(
    //   String(tale?.title || "")
    //     .toLowerCase()
    //     .replace(/\s+/g, "-")
    // );
    navigate(`/itinerary/${slug}`);
  }
  if (!tale)
    return (
      <div className="no tale-card">
        {/* <p>
          "Jobs fill your pocket, but adventures fill your soul." - Jamie Lyn
          Beatty
        </p>
        <p>So lets get started on your next adventure!</p>
        <hr /> */}
        <p>"Life's short. Eat dessert first, work less and travel more."</p>
      </div>
    );

  const handleImageError = (event) => {
    // Log the event details immediately
    console.error("Image load failed. Event details:", event);
    console.error("Failing Source URL:", event.target.src);
    console.error("Failing Title:", tale.title);

    // Set the state to switch to the placeholder image
    setImageError(true);
  };

  return (
    <div
      className="tale-card"
      onClick={() => onOpenItinerary(tale)}
      // onClick={() => onClick?.onOpenItinerary(tale)}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-4px)";
        e.currentTarget.style.boxShadow = "0 8px 20px rgba(0, 0, 0, 0.12)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.08)";
      }}
    >
      <div className="tale-card-image-wrapper">
        {/* {imageError ? ( */}
        <>
          {/* // <PlaceholderImage />
          // <div>Ooops</div>
          //{" "} */}
        </>
        {/* ) : ( */}
        <img
          src={tale.image} //{tale.image === "" ? null : tale.image}
          alt={tale.title}
          onError={handleImageError} //{() => setImageError(true)}
        />
        {/* )} */}
      </div>

      <div className="tale-card-info">
        <div className="tale-card-info-heading">
          <MapPin size={18} style={{ color: "var(--info)", flexShrink: 0 }} />
          <h2>{tale.title}</h2>
        </div>

        <div className="tale-card-info-date">
          <Calendar size={16} />
          <p>
            {formatShortMonthDateRange(tale.startDateTime, tale.endDateTime)}
          </p>
        </div>

        <div className="tale-card-counter-button">
          {new Date(tale.startDateTime) > today ? (
            <span>
              {tMinusCount(tale.startDateTime)} days until your next tale!
            </span>
          ) : new Date(tale.startDateTime) <= today &&
            new Date(tale.endDateTime) >= today ? (
            <span>Hope you are having fun...</span>
          ) : (
            <span>
              It's been {tPlusCount(tale.endDateTime)} days since this tale!
            </span>
            // [ ] add feature when there is no tale planned or in progress to explore new tale ideas
          )}
        </div>
      </div>
    </div>
  );
};

export default TaleCard;
